import React, { useState, useEffect, useRef, useMemo } from 'react';
import Layout from '../components/Layout';
import { salesService, operadorasService, usersService, apiConfigService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateForDisplay, formatDocumento, getTodayBrasilia, toDateInputValue } from '../utils/masks';
import { dataIsoEstaNoPeriodo, getPeriodoComercialExtk } from '../utils/periodoComercialPagamento';
import { formatMarcaLabel } from '../utils/marcas';
import '../styles/VendasPagamento.css';

const getPercentualVendedor = (vendedor, tipoCliente) => {
  if (!vendedor) return 100;
  const isCpf = tipoCliente === 'cpf';
  const percCpf = vendedor.percentual_vendedor_cpf != null ? parseFloat(vendedor.percentual_vendedor_cpf) : null;
  const percCnpj = vendedor.percentual_vendedor_cnpj != null ? parseFloat(vendedor.percentual_vendedor_cnpj) : null;
  if (isCpf && percCpf != null) return percCpf;
  if (!isCpf && percCnpj != null) return percCnpj;
  return vendedor.percentual_vendedor != null ? parseFloat(vendedor.percentual_vendedor) : 100;
};

const calcValorAReceber = (venda) => {
  const vt = parseFloat(venda.valor_total || 0);
  const perc = getPercentualVendedor(venda.vendedor, venda.tipo_cliente);
  return vt * (perc / 100);
};

// % equipe do supervisor incide sobre o valor total da venda (não sobre a comissão do vendedor)
const calcComissaoSupervisorEquipe = (venda, percentualEquipe) => {
  const vt = parseFloat(venda.valor_total || 0);
  const p = parseFloat(percentualEquipe) || 0;
  return vt * (p / 100);
};

/** Pagamento parcial ao vendedor: comissão do supervisor proporcional ao pago ao vendedor */
const getValorSupervisorPagoParcialProporcional = (venda, percentualEquipe) => {
  const comissaoSupCheia = calcComissaoSupervisorEquipe(venda, percentualEquipe);
  const comissaoVendedor = calcValorAReceber(venda);
  const vp = parseFloat(venda.valor_parcial_pago || 0);
  if (comissaoVendedor <= 0) return 0;
  return comissaoSupCheia * (vp / comissaoVendedor);
};

// Para supervisor: vendas próprias = comissão vendedor; vendas de subordinados = % equipe sobre valor da venda
const getValorAReceberExibido = (venda, currentUser) => {
  const base = calcValorAReceber(venda);
  if (!currentUser || currentUser.role !== 'supervisor') return base;
  const vendedorId = venda.vendedor_id ?? venda.vendedor?.id;
  if (Number(vendedorId) === Number(currentUser.id)) return base; // venda própria
  const percEquipe = parseFloat(currentUser.percentual_equipe) || 0;
  const vt = parseFloat(venda.valor_total || 0);
  return vt * (percEquipe / 100);
};

const getApiBaseUrl = () => process.env.REACT_APP_API_URL || 'http://localhost:9040';

const getCurrentMonthYearBrasilia = () => {
  const now = new Date();
  const brtMs = now.getTime() - 3 * 60 * 60 * 1000;
  const d = new Date(brtMs);
  return { mes: d.getUTCMonth() + 1, ano: d.getUTCFullYear() };
};

const MESES_LABEL = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const getImagemContatoDuploUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${getApiBaseUrl()}${path.startsWith('/') ? path : `/${path}`}`;
};

const VendasPagamento = () => {
  const { user } = useAuth();
  const currentBr = useMemo(getCurrentMonthYearBrasilia, []);
  const isVendedorOnly = user?.role === 'vendedor';
  const isSupervisor = user?.role === 'supervisor';
  /** Supervisor precisa ver vendedor e PIX da equipe como admin; vendedor só vê as próprias vendas */
  const showVendedorPixColumns = !isVendedorOnly;
  /** Só admin/diretor editam status de pagamento e importam XLSX */
  const canManagePagamentos = ['admin', 'diretor'].includes(user?.role);
  /** Admin/diretor, vendedor (próprio) e supervisor (equipe) podem gerar comprovante */
  const canVerComprovante = canManagePagamentos || isVendedorOnly || isSupervisor;
  const comprovanteRef = useRef(null);
  const [vendas, setVendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [busca, setBusca] = useState('');
  const [buscaDebounced, setBuscaDebounced] = useState('');
  const [filtroVendedor, setFiltroVendedor] = useState('');
  const [totaisApi, setTotaisApi] = useState(null);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [totalPaginasApi, setTotalPaginasApi] = useState(1);
  const [vendedores, setVendedores] = useState([]);
  const [supervisoresMap, setSupervisoresMap] = useState({}); // Mapa de ID supervisor -> {percentual_equipe, nome, pix}
  const [selectedVenda, setSelectedVenda] = useState(null);
  const [selectedVendaSupervisor, setSelectedVendaSupervisor] = useState(null); // Para modal do supervisor
  const [vendaPrintContatoDuplo, setVendaPrintContatoDuplo] = useState(null); // Visualização do print (vendedor/supervisor)
  const [pagamentoData, setPagamentoData] = useState({
    status_pagamento: 'a_pagar',
    data_pagamento: '',
    data_pagamento_parcial: '',
    valor_total: '',
    valor_parcial_pago: ''
  });
  const [pagamentoSupervisorData, setPagamentoSupervisorData] = useState({
    status_pagamento_supervisor: 'a_pagar',
    data_pagamento_supervisor: '',
    valor_pago_supervisor: ''
  });
  const [showComprovante, setShowComprovante] = useState(false);
  const [vendasComprovante, setVendasComprovante] = useState([]);
  const [loadingComprovante, setLoadingComprovante] = useState(false);

  // Adicionar/remover classe no body para impressão
  useEffect(() => {
    if (showComprovante) {
      document.body.classList.add('printing-comprovante');
    } else {
      document.body.classList.remove('printing-comprovante');
    }
    return () => document.body.classList.remove('printing-comprovante');
  }, [showComprovante]);

  // Função para imprimir comprovante em nova janela
  const imprimirComprovante = () => {
    const printContent = document.getElementById('comprovante-print-area');
    if (!printContent) return;

    const { primary, primaryDark } = (() => {
      const s = getComputedStyle(document.documentElement);
      return {
        primary: s.getPropertyValue('--primary').trim() || '#0D9488',
        primaryDark: s.getPropertyValue('--primary-dark').trim() || '#0F766E',
      };
    })();
    
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) {
      alert('Por favor, permita popups para imprimir o comprovante');
      return;
    }
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Comprovante de Pagamentos</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              background: #fff;
              color: #333;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 12px;
            }
            th, td {
              padding: 8px;
              border: 1px solid ${primaryDark};
              text-align: left;
            }
            th {
              background: ${primary};
              color: #fff;
            }
            h2 {
              color: ${primaryDark};
              text-align: center;
              margin-bottom: 10px;
            }
            .comprovante-header {
              text-align: center;
              border-bottom: 2px solid ${primary};
              padding-bottom: 15px;
              margin-bottom: 20px;
            }
            img {
              max-height: 60px;
            }
            .total-row {
              font-weight: bold;
              border-top: 2px solid ${primary};
              margin-top: 20px;
              padding-top: 10px;
            }
            @media print {
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };
  const [comprovanteModo, setComprovanteModo] = useState('geral'); // 'geral' | vendedor_id
  const [comprovanteMes, setComprovanteMes] = useState(() => new Date().getMonth() + 1);
  const [comprovanteAno, setComprovanteAno] = useState(() => new Date().getFullYear());
  const [updating, setUpdating] = useState(false);
  const [operadoras, setOperadoras] = useState([]);
  const [operadoraResumo, setOperadoraResumo] = useState(null);
  const [loadingResumo, setLoadingResumo] = useState(false);

  const [filtroMes, setFiltroMes] = useState(() => currentBr.mes);
  const [filtroAno, setFiltroAno] = useState(() => currentBr.ano);
  const [filtroCanal, setFiltroCanal] = useState('');
  const [exportarStatus, setExportarStatus] = useState([]); // array: permite múltiplos status
  const [showExportCsvModal, setShowExportCsvModal] = useState(false);
  const [exportCsvModo, setExportCsvModo] = useState('equipe'); // 'equipe' | 'meu' | 'por_vendedor'
  const [exportCsvVendedoresIds, setExportCsvVendedoresIds] = useState([]);
  const [exportCsvIncluirAReceber, setExportCsvIncluirAReceber] = useState(true);

  // Estados de paginação
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(10);
  const [vendasExportCache, setVendasExportCache] = useState([]);

  const fileInputParcialRef = useRef(null);
  const fileInputPagoRef = useRef(null);
  const fileInputEstornoRef = useRef(null);
  const fileInputExtratoRef = useRef(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importPendingFile, setImportPendingFile] = useState(null);
  const [importModo, setImportModo] = useState(null);
  const [importResolucoes, setImportResolucoes] = useState({});
  const [showImportCnpjModal, setShowImportCnpjModal] = useState(false);
  const [showImportConfirmModal, setShowImportConfirmModal] = useState(false);
  const [importResultado, setImportResultado] = useState(null);

  const resetImportFlow = () => {
    setImportPreview(null);
    setImportPendingFile(null);
    setImportModo(null);
    setImportResolucoes({});
    setShowImportCnpjModal(false);
    setShowImportConfirmModal(false);
  };

  const handleFileImportParcial = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !canManagePagamentos) return;
    setImportModo('parcial');
    setImportLoading(true);
    try {
      const data = await salesService.previewImportPagamento(file, 'parcial');
      setImportPreview(data);
      setImportPendingFile(file);
      const res = {};
      (data.conflitos || []).forEach((c) => {
        const primeiro = c.candidatos?.[0]?.id;
        if (primeiro != null) res[c.chave_grupo] = String(primeiro);
      });
      setImportResolucoes(res);
      if (data.conflitos?.length) {
        setShowImportCnpjModal(true);
      } else {
        setShowImportConfirmModal(true);
      }
    } catch (error) {
      alert(error.response?.data?.error || error.message || 'Erro ao analisar a planilha');
    } finally {
      setImportLoading(false);
    }
  };

  const handleFileImportPago = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !canManagePagamentos) return;
    setImportModo('pago');
    setImportLoading(true);
    try {
      const data = await salesService.previewImportPagamento(file, 'pago');
      setImportPreview(data);
      setImportPendingFile(file);
      const res = {};
      (data.conflitos || []).forEach((c) => {
        const primeiro = c.candidatos?.[0]?.id;
        if (primeiro != null) res[c.chave_grupo] = String(primeiro);
      });
      setImportResolucoes(res);
      if (data.conflitos?.length) {
        setShowImportCnpjModal(true);
      } else {
        setShowImportConfirmModal(true);
      }
    } catch (error) {
      alert(error.response?.data?.error || error.message || 'Erro ao analisar a planilha');
    } finally {
      setImportLoading(false);
    }
  };

  const handleFileImportEstorno = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !canManagePagamentos) return;
    setImportModo('estorno');
    setImportLoading(true);
    try {
      const data = await salesService.previewImportPagamento(file, 'estorno');
      setImportPreview(data);
      setImportPendingFile(file);
      const res = {};
      (data.conflitos || []).forEach((c) => {
        const primeiro = c.candidatos?.[0]?.id;
        if (primeiro != null) res[c.chave_grupo] = String(primeiro);
      });
      setImportResolucoes(res);
      if (data.conflitos?.length) {
        setShowImportCnpjModal(true);
      } else {
        setShowImportConfirmModal(true);
      }
    } catch (error) {
      alert(error.response?.data?.error || error.message || 'Erro ao analisar a planilha');
    } finally {
      setImportLoading(false);
    }
  };

  const handleFileImportExtrato = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !canManagePagamentos) return;
    setImportModo('extrato');
    setImportLoading(true);
    try {
      const data = await salesService.previewImportPagamento(file, 'extrato');
      setImportPreview(data);
      setImportPendingFile(file);
      const res = {};
      (data.conflitos || []).forEach((c) => {
        const primeiro = c.candidatos?.[0]?.id;
        if (primeiro != null) res[c.chave_grupo] = String(primeiro);
      });
      setImportResolucoes(res);
      if (data.conflitos?.length) {
        setShowImportCnpjModal(true);
      } else {
        setShowImportConfirmModal(true);
      }
    } catch (error) {
      alert(error.response?.data?.error || error.message || 'Erro ao analisar a planilha');
    } finally {
      setImportLoading(false);
    }
  };

  const fecharModalCnpjEconfirmar = () => {
    setShowImportCnpjModal(false);
    setShowImportConfirmModal(true);
  };

  const executarImportApply = async () => {
    if (!importPendingFile || !importModo || !importPreview?.data_ref_sugerida) return;
    if (importPreview.erros?.length) {
      alert('Corrija os erros indicados no resumo antes de aplicar.');
      return;
    }
    if (!importPreview.grupos?.length) {
      alert('Nenhum grupo válido para importar.');
      return;
    }
    setImportLoading(true);
    try {
      const resolucoesFiltradas = {};
      Object.keys(importResolucoes).forEach((k) => {
        if (importResolucoes[k] != null && importResolucoes[k] !== '') {
          resolucoesFiltradas[k] = parseInt(String(importResolucoes[k]), 10);
        }
      });
      const data = await salesService.applyImportPagamento(
        importPendingFile,
        importModo,
        importPreview.data_ref_sugerida,
        resolucoesFiltradas
      );
      setShowImportConfirmModal(false);
      setImportPreview(null);
      setImportPendingFile(null);
      setImportModo(null);
      setImportResolucoes({});
      setImportResultado(data);
      await loadVendasConcluidas();
    } catch (error) {
      alert(error.response?.data?.error || error.message || 'Erro ao aplicar importação');
    } finally {
      setImportLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setBuscaDebounced(busca.trim()), 400);
    return () => clearTimeout(t);
  }, [busca]);

  useEffect(() => {
    loadVendasConcluidas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroStatus, buscaDebounced, filtroVendedor, filtroMes, filtroAno, filtroCanal, exportarStatus, paginaAtual, itensPorPagina]);

  useEffect(() => {
    setPaginaAtual(1);
  }, [filtroStatus, buscaDebounced, filtroVendedor, filtroMes, filtroAno, filtroCanal, exportarStatus, itensPorPagina]);

  useEffect(() => {
    loadOperadoras();
  }, []);

  useEffect(() => {
    if (!showComprovante) return undefined;
    let cancelled = false;
    (async () => {
      setLoadingComprovante(true);
      try {
        const result = await salesService.getVendasConcluidasComBusca({
          all: 1,
          mes: comprovanteMes,
          ano: comprovanteAno,
          status_pagamento_in: ['pago', 'pago_parcial']
        });
        if (!cancelled) setVendasComprovante(result.data || []);
      } catch (error) {
        console.error('Erro ao carregar comprovante:', error);
        if (!cancelled) setVendasComprovante([]);
      } finally {
        if (!cancelled) setLoadingComprovante(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showComprovante, comprovanteMes, comprovanteAno]);

  useEffect(() => {
    if (isVendedorOnly) {
      if (user?.id) {
        setVendedores([{ id: user.id, nome: user.nome, pix: user.pix }]);
      }
      return;
    }
    // Supervisor não tem acesso a GET /users: mapa próprio + equipe via endpoint dedicado
    if (isSupervisor && user?.id) {
      const pe = parseFloat(user.percentual_equipe);
      if (pe > 0) {
        setSupervisoresMap({
          [user.id]: {
            nome: user.nome,
            pix: user.pix,
            percentual_equipe: user.percentual_equipe
          }
        });
      } else {
        setSupervisoresMap({});
      }
      loadEquipeSupervisor();
      return;
    }
    loadVendedores();
  }, [isVendedorOnly, isSupervisor, user?.id, user?.nome, user?.pix, user?.percentual_equipe]);

  const loadEquipeSupervisor = async () => {
    if (!user?.id) return;
    try {
      const subordinados = await usersService.getVendedoresBySupervisor(user.id);
      const eu = { id: user.id, nome: user.nome, pix: user.pix };
      setVendedores([eu, ...(subordinados || [])]);
    } catch (error) {
      console.error('Erro ao carregar equipe do supervisor:', error);
      setVendedores([{ id: user.id, nome: user.nome, pix: user.pix }]);
    }
  };

  const loadVendedores = async () => {
    try {
      const data = await usersService.getAll({ para_pagamento: 1 });
      setVendedores(data);
      // Criar mapa de supervisores com seus percentuais
      const supervisores = {};
      data.forEach(u => {
        if (u.percentual_equipe && u.percentual_equipe > 0) {
          supervisores[u.id] = {
            nome: u.nome,
            pix: u.pix,
            percentual_equipe: u.percentual_equipe
          };
        }
      });
      setSupervisoresMap(supervisores);
    } catch (error) {
      console.error('Erro ao carregar vendedores:', error);
    }
  };

  const buildPagamentoQueryParams = (extra = {}) => {
    const params = { ...extra };
    if (exportarStatus.length > 0) {
      params.status_pagamento_in = exportarStatus;
    } else if (filtroStatus) {
      params.status_pagamento = filtroStatus;
    }
    if (buscaDebounced) params.busca = buscaDebounced;
    if (filtroVendedor) params.vendedor_id = filtroVendedor;
    if (filtroMes) {
      params.mes = filtroMes;
      params.ano = filtroAno;
    }
    if (filtroCanal) params.canal = filtroCanal;
    return params;
  };

  const loadVendasConcluidas = async () => {
    try {
      setLoading(true);
      const params = buildPagamentoQueryParams({
        page: paginaAtual,
        limit: itensPorPagina
      });
      const result = await salesService.getVendasConcluidasComBusca(params);
      setVendas(result.data || []);
      setTotaisApi(result.totais || null);
      setTotalRegistros(result.pagination?.total || 0);
      setTotalPaginasApi(result.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Erro ao carregar vendas concluídas:', error);
      alert('Erro ao carregar vendas concluídas');
    } finally {
      setLoading(false);
    }
  };

  /** Carrega todas as vendas do filtro atual (CSV / comprovante). */
  const fetchTodasVendasFiltradas = async () => {
    const result = await salesService.getVendasConcluidasComBusca(
      buildPagamentoQueryParams({ all: 1 })
    );
    return result.data || [];
  };

  const openComprovante = () => {
    if (isVendedorOnly && user?.id) {
      setComprovanteModo(String(user.id));
    } else {
      setComprovanteModo('geral');
    }
    setShowComprovante(true);
  };

  const loadOperadoras = async () => {
    try {
      const data = await operadorasService.getAll();
      setOperadoras(data);
    } catch (error) {
      console.error('Erro ao carregar operadoras:', error);
    }
  };

  const getStatusPagamentoClass = (status) => {
    const classes = {
      'a_pagar': 'status-a-pagar',
      'pago_parcial': 'status-pago-parcial',
      'pago': 'status-pago',
      'estornado': 'status-estornado',
      'contato_duplo': 'status-contato-duplo',
      'ordem_duplicada': 'status-ordem-duplicada'
    };
    return classes[status] || '';
  };

  const getStatusPagamentoText = (status) => {
    const texts = {
      'a_pagar': 'A Pagar',
      'pago_parcial': 'Pago Parcial',
      'pago': 'Pago',
      'estornado': 'Estornado',
      'contato_duplo': 'Contato Duplo',
      'ordem_duplicada': 'Ordem Duplicada'
    };
    return texts[status] || status;
  };

  const openPagamento = (venda) => {
    setSelectedVenda(venda);
    const vt = venda.valor_total != null ? parseFloat(venda.valor_total).toFixed(2).replace('.', ',') : '';
    const vp = venda.valor_parcial_pago != null ? parseFloat(venda.valor_parcial_pago).toFixed(2).replace('.', ',') : '';
    setPagamentoData({
      status_pagamento: venda.status_pagamento || 'a_pagar',
      data_pagamento: toDateInputValue(venda.data_pagamento) || '',
      data_pagamento_parcial: toDateInputValue(venda.data_pagamento_parcial) || '',
      valor_total: vt,
      valor_parcial_pago: vp
    });
  };

  const closePagamento = () => {
    setSelectedVenda(null);
    setPagamentoData({
      status_pagamento: 'a_pagar',
      data_pagamento: '',
      data_pagamento_parcial: '',
      valor_total: '',
      valor_parcial_pago: ''
    });
  };

  const handlePagamentoChange = (e) => {
    const { name, value } = e.target;
    setPagamentoData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdatePagamento = async (e) => {
    e.preventDefault();

    if (!pagamentoData.status_pagamento) {
      alert('Selecione um status de pagamento');
      return;
    }

    if (pagamentoData.status_pagamento === 'pago' && !pagamentoData.data_pagamento) {
      pagamentoData.data_pagamento = getTodayBrasilia();
    }

    try {
      setUpdating(true);
      const valorTotalNum = pagamentoData.valor_total ? parseFloat(String(pagamentoData.valor_total).replace(',', '.')) : null;
      const valorParcialNum = pagamentoData.valor_parcial_pago ? parseFloat(String(pagamentoData.valor_parcial_pago).replace(',', '.')) : null;
      
      const dataPagar = pagamentoData.status_pagamento === 'pago' ? pagamentoData.data_pagamento : null;
      const dataParcial = pagamentoData.status_pagamento === 'pago_parcial' ? (pagamentoData.data_pagamento_parcial || null) : null;
      
      await salesService.updateStatusPagamento(
        selectedVenda.id,
        pagamentoData.status_pagamento,
        dataPagar,
        dataParcial,
        pagamentoData.status_pagamento === 'pago_parcial' ? valorParcialNum : null,
        valorTotalNum
      );

      alert('✅ Status de pagamento atualizado com sucesso!');
      closePagamento();
      await loadVendasConcluidas();
    } catch (error) {
      console.error('Erro ao atualizar status de pagamento:', error);
      alert('❌ Erro ao atualizar status de pagamento: ' + (error.response?.data?.error || error.message));
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdatePagamentoSupervisor = async (e) => {
    e.preventDefault();

    if (!pagamentoSupervisorData.status_pagamento_supervisor) {
      alert('Selecione um status de pagamento');
      return;
    }

    if (pagamentoSupervisorData.status_pagamento_supervisor === 'pago' && !pagamentoSupervisorData.data_pagamento_supervisor) {
      pagamentoSupervisorData.data_pagamento_supervisor = getTodayBrasilia();
    }

    try {
      setUpdating(true);
      
      const dataPagar = pagamentoSupervisorData.status_pagamento_supervisor === 'pago' ? pagamentoSupervisorData.data_pagamento_supervisor : null;
      const valorPagoNum = pagamentoSupervisorData.valor_pago_supervisor 
        ? parseFloat(String(pagamentoSupervisorData.valor_pago_supervisor).replace(',', '.')) 
        : null;
      
      await salesService.updateStatusPagamentoSupervisor(
        selectedVendaSupervisor.id,
        pagamentoSupervisorData.status_pagamento_supervisor,
        dataPagar,
        valorPagoNum
      );

      alert('✅ Status de pagamento do supervisor atualizado com sucesso!');
      setSelectedVendaSupervisor(null);
      setPagamentoSupervisorData({
        status_pagamento_supervisor: 'a_pagar',
        data_pagamento_supervisor: '',
        valor_pago_supervisor: ''
      });
      await loadVendasConcluidas();
    } catch (error) {
      console.error('Erro ao atualizar status de pagamento do supervisor:', error);
      alert('❌ Erro ao atualizar status de pagamento do supervisor: ' + (error.response?.data?.error || error.message));
    } finally {
      setUpdating(false);
    }
  };

  const [resumoMes, setResumoMes] = useState(() => new Date().getMonth() + 1);
  const [resumoAno, setResumoAno] = useState(() => new Date().getFullYear());
  const [operadoraResumoId, setOperadoraResumoId] = useState(null);
  /** Modal resumo por operadora: saldos em aberto vs. valores já quitados no período */
  const [resumoModo, setResumoModo] = useState('a_receber');
  const [showResumoMarca, setShowResumoMarca] = useState(false);
  const [resumoMarcaId, setResumoMarcaId] = useState(null); // 'onda' | 'vision'
  const [resumoMarca, setResumoMarca] = useState(null);
  const [loadingResumoMarca, setLoadingResumoMarca] = useState(false);
  const [resumoMarcaMes, setResumoMarcaMes] = useState(() => new Date().getMonth() + 1);
  const [resumoMarcaAno, setResumoMarcaAno] = useState(() => new Date().getFullYear());
  const [resumoMarcaModo, setResumoMarcaModo] = useState('a_receber');
  const resumoMarcaLabel = resumoMarcaId ? formatMarcaLabel(resumoMarcaId) : '';

  const totais = totaisApi || {
    aPagar: 0, pagoParcial: 0, pagas: 0, estornadas: 0,
    contatoDuplo: 0, ordemDuplicada: 0, valorTotalPago: 0
  };

  const opcoesMeses = useMemo(() => {
    const opts = [];
    if (canManagePagamentos) {
      opts.push({ mes: '', ano: '', label: 'Todos os meses' });
    }
    const { mes: m0, ano: a0 } = getCurrentMonthYearBrasilia();
    for (let i = 0; i < 24; i++) {
      let mes = m0 - i;
      let ano = a0;
      while (mes <= 0) { mes += 12; ano -= 1; }
      opts.push({ mes, ano, label: `${MESES_LABEL[mes - 1]} ${ano}` });
    }
    return opts;
  }, [canManagePagamentos]);

  // Página já vem filtrada/paginada do servidor
  const vendasPaginadas = vendas;
  const totalPaginas = totalPaginasApi;

  const slugNomeArquivo = (nome) =>
    String(nome || 'vendedor')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_|_$/g, '')
      .toLowerCase() || 'vendedor';

  const baixarCsvBlob = (csvContent, filename) => {
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const montarCsvVendas = (listaVendas, incluirValorAReceber = true) => {
    const getTipoDocumentoLabel = (venda) => {
      if (venda.tipo_cliente === 'cpf') return 'CPF';
      if (venda.tipo_cliente === 'cnpj') return 'CNPJ';
      const digits = String(venda.documento || '').replace(/\D/g, '');
      if (digits.length === 11) return 'CPF';
      if (digits.length === 14) return 'CNPJ';
      return '';
    };

    const escapeCsvCell = (cell) => {
      const str = cell == null ? '' : String(cell);
      return `"${str.replace(/"/g, '""')}"`;
    };

    const headers = [
      'ID',
      'Cliente',
      'Tipo Documento',
      'Marca',
      'Documento',
      'Vendedor',
      'PIX',
      'Valor Total',
      ...(incluirValorAReceber ? ['Valor a Receber'] : []),
      'Status Pagamento',
      'Data Pag. Parcial',
      'Data Pag. Total',
      'Data Conclusão'
    ];

    const formatMarcaCsv = (marca) => {
      if (marca === 'extk') return 'EXTK';
      if (marca === 'onda') return 'ONDA TELECOM';
      if (marca === 'vision') return 'VISION TELECOM';
      return '';
    };

    const rows = listaVendas.map(venda => [
      venda.id,
      venda.nome_cliente || venda.razao_social || '',
      getTipoDocumentoLabel(venda),
      formatMarcaCsv(venda.marca),
      formatDocumento(venda.documento),
      venda.vendedor?.nome || 'N/A',
      venda.vendedor?.pix || '',
      parseFloat(venda.valor_total || 0).toFixed(2).replace('.', ','),
      ...(incluirValorAReceber
        ? [getValorAReceberExibido(venda, user).toFixed(2).replace('.', ',')]
        : []),
      getStatusPagamentoText(venda.status_pagamento),
      venda.data_pagamento_parcial ? formatDateForDisplay(venda.data_pagamento_parcial) : '',
      venda.data_pagamento ? formatDateForDisplay(venda.data_pagamento) : '',
      venda.data_conclusao ? formatDateForDisplay(venda.data_conclusao) : ''
    ]);

    return [
      headers.map(escapeCsvCell).join(';'),
      ...rows.map(row => row.map(escapeCsvCell).join(';'))
    ].join('\n');
  };

  const baixarCsvLista = (listaVendas, sufixoNome, incluirValorAReceber = true) => {
    if (!listaVendas.length) return false;
    const dataAtual = new Date().toISOString().split('T')[0];
    const sufixo = sufixoNome ? `_${slugNomeArquivo(sufixoNome)}` : '';
    baixarCsvBlob(montarCsvVendas(listaVendas, incluirValorAReceber), `vendas_pagamentos${sufixo}_${dataAtual}.csv`);
    return true;
  };

  const abrirExportCsv = async () => {
    if (totalRegistros === 0) {
      alert('Nenhuma venda para exportar com os filtros atuais.');
      return;
    }
    try {
      const todas = await fetchTodasVendasFiltradas();
      setVendasExportCache(todas);
      if (todas.length === 0) {
        alert('Nenhuma venda para exportar com os filtros atuais.');
        return;
      }
      if (isSupervisor) {
        setExportCsvModo('equipe');
        setExportCsvVendedoresIds(vendedores.map((v) => Number(v.id)));
        setExportCsvIncluirAReceber(true);
        setShowExportCsvModal(true);
        return;
      }
      baixarCsvLista(todas);
    } catch (error) {
      console.error('Erro ao exportar CSV:', error);
      alert('Erro ao carregar vendas para exportação');
    }
  };

  const confirmarExportCsvSupervisor = async () => {
    const dataAtual = new Date().toISOString().split('T')[0];
    let listaBase = vendasExportCache;
    if (!listaBase.length) {
      try {
        listaBase = await fetchTodasVendasFiltradas();
        setVendasExportCache(listaBase);
      } catch (error) {
        alert('Erro ao carregar vendas para exportação');
        return;
      }
    }
    if (exportCsvModo === 'equipe') {
      if (!baixarCsvLista(listaBase, 'equipe', exportCsvIncluirAReceber)) {
        alert('Nenhuma venda para exportar com os filtros atuais.');
        return;
      }
      setShowExportCsvModal(false);
      return;
    }
    if (exportCsvModo === 'meu') {
      const minhas = listaBase.filter(
        (v) => Number(v.vendedor_id ?? v.vendedor?.id) === Number(user?.id)
      );
      if (!baixarCsvLista(minhas, user?.nome || 'meu', exportCsvIncluirAReceber)) {
        alert('Nenhuma venda sua para exportar com os filtros atuais.');
        return;
      }
      setShowExportCsvModal(false);
      return;
    }
    // por_vendedor: um arquivo por vendedor selecionado
    if (!exportCsvVendedoresIds.length) {
      alert('Selecione ao menos um vendedor.');
      return;
    }
    let baixados = 0;
    const semVendas = [];
    for (let i = 0; i < exportCsvVendedoresIds.length; i++) {
      const vendId = exportCsvVendedoresIds[i];
      const vend = vendedores.find((x) => Number(x.id) === Number(vendId));
      const lista = listaBase.filter(
        (v) => Number(v.vendedor_id ?? v.vendedor?.id) === Number(vendId)
      );
      if (!lista.length) {
        semVendas.push(vend?.nome || `#${vendId}`);
        continue;
      }
      baixarCsvBlob(
        montarCsvVendas(lista, exportCsvIncluirAReceber),
        `vendas_pagamentos_${slugNomeArquivo(vend?.nome || vendId)}_${dataAtual}.csv`
      );
      baixados += 1;
      if (i < exportCsvVendedoresIds.length - 1) {
        await new Promise((r) => setTimeout(r, 350));
      }
    }
    if (baixados === 0) {
      alert('Nenhuma venda para exportar dos vendedores selecionados.');
      return;
    }
    if (semVendas.length > 0) {
      alert(`${baixados} arquivo(s) baixado(s). Sem vendas no filtro: ${semVendas.join(', ')}.`);
    }
    setShowExportCsvModal(false);
  };

  const loadResumoPorOperadora = async (operadoraId, mes, ano, modo = 'a_receber') => {
    const data = await salesService.getResumoPagamentoPorOperadora(operadoraId, mes, ano, modo);
    setOperadoraResumo(data);
  };

  const loadResumoMarca = async (marca, mes, ano, modo = 'a_receber') => {
    const data = await salesService.getResumoPagamentoMarca(marca, mes, ano, modo);
    setResumoMarca(data);
  };

  const closeResumoMarcaModal = () => {
    setShowResumoMarca(false);
    setResumoMarca(null);
    setResumoMarcaId(null);
  };

  const openResumoMarcaModal = async (marca) => {
    const now = new Date();
    const mes = now.getMonth() + 1;
    const ano = now.getFullYear();
    const label = formatMarcaLabel(marca);
    setResumoMarcaId(marca);
    setResumoMarcaMes(mes);
    setResumoMarcaAno(ano);
    setResumoMarcaModo('a_receber');
    setShowResumoMarca(true);
    try {
      setLoadingResumoMarca(true);
      setResumoMarca(null);
      await loadResumoMarca(marca, mes, ano, 'a_receber');
    } catch (error) {
      console.error(`Erro ao carregar resumo ${label}:`, error);
      alert(`Erro ao carregar resumo ${label}: ` + (error.response?.data?.error || error.message));
    } finally {
      setLoadingResumoMarca(false);
    }
  };

  const applyResumoMarcaPeriodo = async (mes, ano) => {
    if (!resumoMarcaId) return;
    setResumoMarcaMes(mes);
    setResumoMarcaAno(ano);
    try {
      setLoadingResumoMarca(true);
      await loadResumoMarca(resumoMarcaId, mes, ano, resumoMarcaModo);
    } catch (error) {
      console.error(`Erro ao carregar resumo ${resumoMarcaLabel}:`, error);
      alert(`Erro ao carregar resumo ${resumoMarcaLabel}: ` + (error.response?.data?.error || error.message));
    } finally {
      setLoadingResumoMarca(false);
    }
  };

  const applyResumoMarcaModo = async (novo) => {
    if (novo === resumoMarcaModo || !resumoMarcaId) return;
    setResumoMarcaModo(novo);
    try {
      setLoadingResumoMarca(true);
      await loadResumoMarca(resumoMarcaId, resumoMarcaMes, resumoMarcaAno, novo);
    } catch (error) {
      console.error(`Erro ao carregar resumo ${resumoMarcaLabel}:`, error);
      alert(`Erro ao carregar resumo ${resumoMarcaLabel}: ` + (error.response?.data?.error || error.message));
    } finally {
      setLoadingResumoMarca(false);
    }
  };

  const openResumoModal = async (operadora) => {
    const now = new Date();
    const mes = now.getMonth() + 1;
    const ano = now.getFullYear();
    setResumoMes(mes);
    setResumoAno(ano);
    setResumoModo('a_receber');
    setOperadoraResumoId(operadora.id);
    try {
      setLoadingResumo(true);
      setOperadoraResumo(null);
      await loadResumoPorOperadora(operadora.id, mes, ano, 'a_receber');
    } catch (error) {
      console.error('Erro ao carregar resumo:', error);
      alert('Erro ao carregar resumo: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoadingResumo(false);
    }
  };

  const applyResumoPeriodo = async (mes, ano) => {
    if (!operadoraResumoId) return;
    setResumoMes(mes);
    setResumoAno(ano);
    try {
      setLoadingResumo(true);
      await loadResumoPorOperadora(operadoraResumoId, mes, ano, resumoModo);
    } catch (error) {
      console.error('Erro ao carregar resumo:', error);
      alert('Erro ao carregar resumo: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoadingResumo(false);
    }
  };

  const switchResumoModo = async (novo) => {
    if (novo === resumoModo || !operadoraResumoId) return;
    try {
      setLoadingResumo(true);
      await loadResumoPorOperadora(operadoraResumoId, resumoMes, resumoAno, novo);
      setResumoModo(novo);
    } catch (error) {
      console.error('Erro ao carregar resumo:', error);
      alert('Erro ao carregar resumo: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoadingResumo(false);
    }
  };

  const closeResumoModal = () => {
    setOperadoraResumo(null);
    setOperadoraResumoId(null);
    setResumoModo('a_receber');
  };

  const resumoLabelColSaldo = resumoModo === 'pagos' ? 'Valor Pago' : 'Saldo a Receber';
  const resumoLabelProprias = resumoModo === 'pagos' ? 'Pago (Próprias)' : 'A Receber (Próprias)';
  const resumoLabelEquipe = resumoModo === 'pagos' ? 'Pago (Equipe)' : 'A Receber (Equipe)';
  const resumoLabelTotal = resumoModo === 'pagos' ? 'Total Pago' : 'Total a Receber';
  const resumoLabelSemanaSaldo = resumoModo === 'pagos' ? 'Valor pago' : 'Saldo';

  return (
    <Layout>
      <div className="vendas-pagamento-page">
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1>💰 Controle de Pagamentos</h1>
            <p>
              {canManagePagamentos
                ? 'Gerencie o status de pagamento das vendas concluídas'
                : isSupervisor
                  ? 'Mesma visão do admin: comissão do vendedor na linha principal e sua parte sobre a equipe na linha do supervisor'
                  : 'Visualize seus pagamentos'}
            </p>
          </div>
          {canVerComprovante && (
            <button type="button" className="btn-primary" onClick={openComprovante}>
              🧾 Gerar Comprovante
            </button>
          )}
        </div>

        <div className="pagamento-periodo-filters">
          <div className="pagamento-filter-group">
            <label htmlFor="pagamento-mes">Mês:</label>
            <select
              id="pagamento-mes"
              value={filtroMes ? `${filtroMes}|${filtroAno}` : ''}
              onChange={(e) => {
                if (!e.target.value) {
                  setFiltroMes('');
                  setFiltroAno('');
                  return;
                }
                const [m, a] = e.target.value.split('|').map(Number);
                setFiltroMes(m);
                setFiltroAno(a);
              }}
              className="pagamento-filter-select"
            >
              {opcoesMeses.map(({ mes, ano, label }) => (
                <option key={mes ? `${mes}-${ano}` : 'todos'} value={mes ? `${mes}|${ano}` : ''}>{label}</option>
              ))}
            </select>
          </div>
          <div className="pagamento-filter-group">
            <label htmlFor="pagamento-canal">Canal:</label>
            <select
              id="pagamento-canal"
              value={filtroCanal}
              onChange={(e) => setFiltroCanal(e.target.value)}
              className="pagamento-filter-select"
            >
              <option value="">Todos</option>
              <option value="extk">EXTK</option>
              <option value="onda">ONDA</option>
              <option value="vision">VISION</option>
            </select>
          </div>
        </div>

        {/* Cards de estatísticas */}
        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-label">A Pagar</div>
            <div className="stat-value status-a-pagar">{totais.aPagar}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Pago Parcial</div>
            <div className="stat-value status-pago-parcial">{totais.pagoParcial}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Pagas</div>
            <div className="stat-value status-pago">{totais.pagas}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Estornadas</div>
            <div className="stat-value status-estornado">{totais.estornadas}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Contato Duplo</div>
            <div className="stat-value status-contato-duplo">{totais.contatoDuplo}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Ordem Duplicada</div>
            <div className="stat-value status-ordem-duplicada">{totais.ordemDuplicada}</div>
          </div>
          <div className="stat-card stat-card-valor">
            <div className="stat-label">Valor Total Pago</div>
            <div className="stat-value">
              R$ {Number(totais.valorTotalPago || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>

        <div className="filters">
          <div className="filter-group">
            <label>Buscar:</label>
            <input 
              type="text" 
              placeholder="Buscar por cliente, documento, vendedor..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="filter-input"
            />
          </div>
          {canManagePagamentos && (
            <div className="filter-group">
              <label>Vendedor:</label>
              <select 
                value={filtroVendedor} 
                onChange={(e) => setFiltroVendedor(e.target.value)}
                className="filter-select"
              >
                <option value="">Todos</option>
                {vendedores.map(vendedor => (
                  <option key={vendedor.id} value={vendedor.id}>{vendedor.nome}</option>
                ))}
              </select>
            </div>
          )}
          <div className="filter-group">
            <label>Status de Pagamento:</label>
            <select 
              value={filtroStatus} 
              onChange={(e) => setFiltroStatus(e.target.value)}
              className="filter-select"
            >
              <option value="">Todos</option>
              <option value="a_pagar">A Pagar</option>
              <option value="pago_parcial">Pago Parcial</option>
              <option value="pago">Pago</option>
              <option value="estornado">Estornado</option>
              <option value="contato_duplo">Contato Duplo</option>
              <option value="ordem_duplicada">Ordem Duplicada</option>
            </select>
          </div>
        </div>

        {/* Filtros de exportação e botão */}
        <div className="filters export-filters" style={{ marginTop: '1rem', padding: '1rem', borderRadius: '8px' }}>
          <div className="export-filters-toolbar">
            <div className="export-filters-top">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, color: 'var(--texto-primario)' }}>Filtros para Exportação:</span>
                <div className="filter-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontSize: '0.85rem' }}>Status:</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 0.75rem', alignItems: 'center' }}>
                    {[
                      { value: 'a_pagar', label: 'A Pagar' },
                      { value: 'pago_parcial', label: 'Pago Parcial' },
                      { value: 'pago', label: 'Pago' },
                      { value: 'estornado', label: 'Estornado' },
                      { value: 'contato_duplo', label: 'Contato Duplo' },
                      { value: 'ordem_duplicada', label: 'Ordem Duplicada' },
                    ].map(({ value, label }) => (
                      <label
                        key={value}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 400 }}
                      >
                        <input
                          type="checkbox"
                          checked={exportarStatus.includes(value)}
                          onChange={(e) => {
                            setExportarStatus((prev) =>
                              e.target.checked ? [...prev, value] : prev.filter((s) => s !== value)
                            );
                          }}
                        />
                        {label}
                      </label>
                    ))}
                    {exportarStatus.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setExportarStatus([])}
                        style={{ fontSize: '0.8rem', color: 'var(--texto-secundario)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                      >
                        Limpar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="export-actions">
              <div className="export-actions-meta">
                <span style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)' }}>
                  {totalRegistros} venda(s) encontrada(s)
                </span>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={abrirExportCsv}
                  disabled={totalRegistros === 0}
                  style={{ opacity: totalRegistros === 0 ? 0.5 : 1, cursor: totalRegistros === 0 ? 'not-allowed' : 'pointer' }}
                >
                  📊 Exportar CSV
                </button>
              </div>
              {canManagePagamentos && (
                <div className="export-actions-imports">
                  <input
                    ref={fileInputParcialRef}
                    type="file"
                    accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    style={{ display: 'none' }}
                    onChange={handleFileImportParcial}
                  />
                  <input
                    ref={fileInputPagoRef}
                    type="file"
                    accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    style={{ display: 'none' }}
                    onChange={handleFileImportPago}
                  />
                  <input
                    ref={fileInputEstornoRef}
                    type="file"
                    accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    style={{ display: 'none' }}
                    onChange={handleFileImportEstorno}
                  />
                  <input
                    ref={fileInputExtratoRef}
                    type="file"
                    accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    style={{ display: 'none' }}
                    onChange={handleFileImportExtrato}
                  />
                  <button
                    type="button"
                    className="btn-secondary import-xlsx-btn"
                    disabled={importLoading}
                    onClick={() => fileInputParcialRef.current?.click()}
                  >
                    {importLoading && importModo === 'parcial' ? '…' : 'Processar pagamento parcial (XLSX)'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary import-xlsx-btn"
                    disabled={importLoading}
                    onClick={() => fileInputPagoRef.current?.click()}
                  >
                    {importLoading && importModo === 'pago' ? '…' : 'Processar pago (XLSX)'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary import-xlsx-btn"
                    disabled={importLoading}
                    onClick={() => fileInputExtratoRef.current?.click()}
                  >
                    {importLoading && importModo === 'extrato' ? '…' : 'Atualizar extrato (XLSX)'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary import-xlsx-btn"
                    disabled={importLoading}
                    onClick={() => fileInputEstornoRef.current?.click()}
                  >
                    {importLoading && importModo === 'estorno' ? '…' : 'Baixa estorno (XLSX)'}
                  </button>
                </div>
              )}
            </div>
            {canManagePagamentos && (
              <div
                className="import-xlsx-planilha-help"
                style={{
                  padding: '0.75rem 1rem',
                  fontSize: '0.85rem',
                  lineHeight: 1.5,
                  color: 'var(--texto-primario)',
                  background: 'var(--fundo)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px'
                }}
              >
                <strong style={{ display: 'block', marginBottom: '0.35rem' }}>Formato da planilha (1ª aba)</strong>
                Use três colunas na ordem A → B → C. A primeira linha pode ser cabeçalho (ex.: “Grupo”, “CPF/CNPJ”, “Valor”) — será ignorada se parecer título.
                <ul style={{ margin: '0.5rem 0 0 1.1rem', padding: 0 }}>
                  <li>
                    <strong>Coluna A — chave de agrupamento:</strong> texto ou número que identifica o grupo de linhas.
                    Várias linhas com a mesma chave A têm os valores da coluna C somados. A chave não localiza a venda no CRM; serve só para agrupar (ex.: parcelas ou linhas do extrato).
                  </li>
                  <li>
                    <strong>Coluna B — CPF ou CNPJ:</strong> documento do cliente (CPF com até 11 dígitos, CNPJ com até 14; zeros à esquerda podem ser omitidos no Excel).
                    Todas as linhas com a mesma chave A devem repetir o mesmo documento em B; documentos diferentes no mesmo grupo geram erro.
                  </li>
                  <li>
                    <strong>Coluna C — valor (R$):</strong> número positivo (vírgula ou ponto). Nos modos <em>pagamento parcial</em> e <em>pago</em>, a soma por grupo A atualiza o valor total da venda. No pago, a venda também é quitada; se já estiver paga, só o valor total é atualizado.
                    No modo <em>atualizar extrato</em>, a soma por grupo A só substitui o valor total da venda; o status de pagamento e os valores já pagos permanecem iguais.
                    No modo <em>baixa estorno</em>, o status de pagamento (vendedor e supervisor) passa a <em>estornado</em>; a coluna C continua obrigatória e entra só na pré-visualização.
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Valor a receber por operadora - admin/diretor: todos; vendedor: só os seus */}
        <section className="fluxo-operadoras-section">
          <h2 className="fluxo-section-title">{isVendedorOnly ? 'Meus valores a receber' : 'Valor a receber por operadora'}</h2>
          <div className="fluxo-operadoras-grid">
            {operadoras.map((op) => (
              <div key={op.id} className="fluxo-operadora-card">
                <div className="fluxo-operadora-nome">{op.nome_display || op.nome}</div>
                <div className="fluxo-card-actions">
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => openResumoModal(op)}
                    disabled={loadingResumo}
                  >
                    Ver valor a receber
                  </button>
                </div>
              </div>
            ))}
            <div className="fluxo-operadora-card">
              <div className="fluxo-operadora-nome">ONDA TELECOM</div>
              <div className="fluxo-card-actions">
                <button
                  type="button"
                  className="btn-primary btn-sm"
                  onClick={() => openResumoMarcaModal('onda')}
                  disabled={loadingResumoMarca}
                >
                  Valor a Receber Onda
                </button>
              </div>
              <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--texto-secundario)' }}>
                Mês fechado 01–31 · pago no mês seguinte
              </small>
            </div>
            <div className="fluxo-operadora-card">
              <div className="fluxo-operadora-nome">VISION TELECOM</div>
              <div className="fluxo-card-actions">
                <button
                  type="button"
                  className="btn-primary btn-sm"
                  onClick={() => openResumoMarcaModal('vision')}
                  disabled={loadingResumoMarca}
                >
                  Valor a Receber Vision
                </button>
              </div>
              <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--texto-secundario)' }}>
                Mês fechado 01–31 · pago no mês seguinte
              </small>
            </div>
          </div>
          {operadoras.length === 0 && !loading && (
            <p className="fluxo-empty">Nenhuma operadora cadastrada.</p>
          )}
        </section>

        {loading ? (
          <div className="loading">Carregando vendas...</div>
        ) : totalRegistros === 0 ? (
          <div className="empty-state">
            <p>📭 Nenhuma venda concluída encontrada</p>
          </div>
        ) : (
          <>
          <div className="table-container">
            <table className="vendas-pagamento-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Cliente</th>
                  <th>Documento</th>
                  {showVendedorPixColumns && <th>Vendedor</th>}
                  {showVendedorPixColumns && <th>PIX (pagamento)</th>}
                  <th>Valor Total</th>
                  <th>Valor a Receber</th>
                  <th>Status Pagamento</th>
                  <th>Data Pag. Parcial</th>
                  <th>Data Pag. Total</th>
                  <th>Data Conclusão</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {vendasPaginadas.map((venda) => {
                  const supervisorId = venda.vendedor?.supervisor_id;
                  const supervisorInfo = supervisorId ? supervisoresMap[supervisorId] : null;
                  const valorAReceberVendedor = calcValorAReceber(venda);
                  const percentualVendedor = getPercentualVendedor(venda.vendedor, venda.tipo_cliente);
                  const percentualSupervisor = supervisorInfo ? supervisorInfo.percentual_equipe : 0;
                  const valorAReceberSupervisor = percentualSupervisor > 0 ? calcComissaoSupervisorEquipe(venda, percentualSupervisor) : 0;
                  const valorPagoSupervisor = parseFloat(venda.valor_pago_supervisor || 0);
                  const saldoSupervisor = Math.max(0, valorAReceberSupervisor - valorPagoSupervisor);
                  const imagemContatoDuploUrl = getImagemContatoDuploUrl(venda.imagem_contato_duplo);
                  
                  return (
                  <React.Fragment key={venda.id}>
                    <tr>
                      <td>#{venda.id}</td>
                      <td>{venda.nome_cliente || venda.razao_social}</td>
                      <td>{formatDocumento(venda.documento, venda.tipo_cliente)}</td>
                      {showVendedorPixColumns && <td>{venda.vendedor?.nome || 'N/A'}</td>}
                      {showVendedorPixColumns && <td className="pix-cell">{venda.vendedor?.pix || '-'}</td>}
                      <td>R$ {parseFloat(venda.valor_total || 0).toFixed(2).replace('.', ',')}</td>
                      <td>
                        <span style={{ fontWeight: 'bold' }}>
                          R$ {valorAReceberVendedor.toFixed(2).replace('.', ',')}
                        </span>
                        {percentualVendedor !== 100 && (
                          <span style={{ fontSize: '0.8em', color: 'var(--texto-secundario)', display: 'block' }}>
                            ({percentualVendedor}%)
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusPagamentoClass(venda.status_pagamento)}`}>
                          {getStatusPagamentoText(venda.status_pagamento)}
                        </span>
                      </td>
                      <td>{venda.data_pagamento_parcial ? formatDateForDisplay(venda.data_pagamento_parcial) : '-'}</td>
                      <td>{venda.data_pagamento ? formatDateForDisplay(venda.data_pagamento) : '-'}</td>
                      <td>{venda.data_conclusao ? formatDateForDisplay(venda.data_conclusao) : '-'}</td>
                      <td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                          {canManagePagamentos && (
                            <button
                              type="button"
                              onClick={() => openPagamento(venda)}
                              className="btn-action"
                            >
                              ✏️ Editar
                            </button>
                          )}
                          {imagemContatoDuploUrl && (
                            <button
                              type="button"
                              onClick={() => setVendaPrintContatoDuplo(venda)}
                              className="btn-action"
                              style={{ background: '#6b7280' }}
                              title="Ver print do contato duplo"
                            >
                              🖼️ Ver print
                            </button>
                          )}
                          {!canManagePagamentos && !imagemContatoDuploUrl && (
                            <span className="muted-text" style={{ fontSize: '0.85rem' }}>—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                    {supervisorInfo && percentualSupervisor > 0 && (
                      <tr className="row-supervisor">
                        {showVendedorPixColumns && (
                          <>
                            <td style={{ color: 'var(--accent-copper)', fontSize: '0.9em' }} colSpan={2}>
                              <em>↑ {supervisorInfo.nome} (Supervisor)</em>
                            </td>
                            <td className="pix-cell" style={{ fontSize: '0.85em' }}>
                              {supervisorInfo.pix || '-'}
                            </td>
                          </>
                        )}
                        {!showVendedorPixColumns && (
                          <td style={{ color: 'var(--accent-copper)', fontSize: '0.9em' }}>
                            <em>↑ {supervisorInfo.nome} (Supervisor)</em>
                          </td>
                        )}
                        <td style={{ fontSize: '0.9em', color: 'var(--texto-secundario)' }}>
                          <em>({percentualSupervisor}% equipe)</em>
                        </td>
                        <td style={{ fontWeight: 'bold', color: 'var(--success)' }}>
                          R$ {valorAReceberSupervisor.toFixed(2).replace('.', ',')}
                        </td>
                        <td>
                          <span className={`status-badge ${getStatusPagamentoClass(venda.status_pagamento_supervisor || 'a_pagar')}`}>
                            {getStatusPagamentoText(venda.status_pagamento_supervisor || 'a_pagar')}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.85em' }}>
                          {venda.status_pagamento_supervisor === 'pago_parcial' && valorPagoSupervisor > 0 
                            ? `R$ ${valorPagoSupervisor.toFixed(2).replace('.', ',')}` 
                            : '-'}
                        </td>
                        <td style={{ fontSize: '0.85em' }}>
                          {venda.data_pagamento_supervisor ? formatDateForDisplay(venda.data_pagamento_supervisor) : '-'}
                        </td>
                        <td style={{ fontSize: '0.85em' }}>-</td>
                        <td>
                          {canManagePagamentos ? (
                            <button 
                              type="button"
                              onClick={() => {
                                setSelectedVendaSupervisor(venda);
                                setPagamentoSupervisorData({
                                  status_pagamento_supervisor: venda.status_pagamento_supervisor || 'a_pagar',
                                  data_pagamento_supervisor: venda.data_pagamento_supervisor || '',
                                  valor_pago_supervisor: venda.valor_pago_supervisor ? venda.valor_pago_supervisor.toFixed(2).replace('.', ',') : ''
                                });
                              }}
                              className="btn-action"
                              style={{ background: '#6b7280' }}
                            >
                              ✏️ Editar
                            </button>
                          ) : (
                            <span className="muted-text" style={{ fontSize: '0.85rem' }}>—</span>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          {totalPaginas > 1 && (
            <div className="pagination" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn-pagination"
                onClick={() => setPaginaAtual(prev => Math.max(prev - 1, 1))}
                disabled={paginaAtual === 1}
                style={{ padding: '0.5rem 1rem', borderRadius: '4px', border: '1px solid var(--border-color)', background: paginaAtual === 1 ? 'var(--cinza-escuro-2)' : 'var(--cinza-escuro-1)', color: 'var(--texto-primario)', cursor: paginaAtual === 1 ? 'not-allowed' : 'pointer' }}
              >
                ◀ Anterior
              </button>
              
              <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                  let pageNum;
                  if (totalPaginas <= 5) {
                    pageNum = i + 1;
                  } else if (paginaAtual <= 3) {
                    pageNum = i + 1;
                  } else if (paginaAtual >= totalPaginas - 2) {
                    pageNum = totalPaginas - 4 + i;
                  } else {
                    pageNum = paginaAtual - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      type="button"
                      onClick={() => setPaginaAtual(pageNum)}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-color)',
                        background: paginaAtual === pageNum ? 'var(--primary)' : 'var(--cinza-escuro-1)',
                        color: paginaAtual === pageNum ? 'var(--btn-on-primary)' : 'var(--texto-primario)',
                        fontWeight: paginaAtual === pageNum ? '600' : '400',
                        cursor: 'pointer'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {totalPaginas > 5 && paginaAtual < totalPaginas - 2 && (
                  <>
                    <span style={{ color: 'var(--texto-secundario)' }}>...</span>
                    <button
                      type="button"
                      onClick={() => setPaginaAtual(totalPaginas)}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-color)',
                        background: 'var(--cinza-escuro-1)',
                        color: 'var(--texto-primario)',
                        cursor: 'pointer'
                      }}
                    >
                      {totalPaginas}
                    </button>
                  </>
                )}
              </div>

              <button
                type="button"
                className="btn-pagination"
                onClick={() => setPaginaAtual(prev => Math.min(prev + 1, totalPaginas))}
                disabled={paginaAtual === totalPaginas}
                style={{ padding: '0.5rem 1rem', borderRadius: '4px', border: '1px solid var(--border-color)', background: paginaAtual === totalPaginas ? 'var(--cinza-escuro-2)' : 'var(--cinza-escuro-1)', color: 'var(--texto-primario)', cursor: paginaAtual === totalPaginas ? 'not-allowed' : 'pointer' }}
              >
                Próxima ▶
              </button>

              <div className="itens-por-pagina" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)' }}>Itens por página:</label>
                <select
                  value={itensPorPagina}
                  onChange={(e) => { setItensPorPagina(Number(e.target.value)); setPaginaAtual(1); }}
                  style={{ padding: '0.35rem', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--cinza-escuro-1)', color: 'var(--texto-primario)' }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          )}
          </>
        )}

        {/* Modal de edição de pagamento */}
        {selectedVenda && (
          <div className="modal-overlay" onClick={closePagamento}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Editar Status de Pagamento</h2>
                <button className="modal-close" onClick={closePagamento}>✕</button>
              </div>
              
              <div className="modal-body">
                <div className="form-group">
                  <label>Venda #{selectedVenda.id}</label>
                  <div className="venda-info">
                    <p><strong>Cliente:</strong> {selectedVenda.nome_cliente || selectedVenda.razao_social}</p>
                    <p><strong>Documento:</strong> {formatDocumento(selectedVenda.documento)}</p>
                    <p><strong>Vendedor:</strong> {selectedVenda.vendedor?.nome || 'N/A'}</p>
                    <p><strong>PIX para pagamento:</strong> {selectedVenda.vendedor?.pix ? (
                      <span className="pix-value">{selectedVenda.vendedor.pix}</span>
                    ) : (
                      <span className="pix-empty">Não cadastrado</span>
                    )}</p>
                    <p><strong>Valor Total da Venda:</strong> R$ {parseFloat(selectedVenda.valor_total || 0).toFixed(2).replace('.', ',')}</p>
                    <p><strong>Valor a receber ({getPercentualVendedor(selectedVenda.vendedor, selectedVenda.tipo_cliente)}%):</strong> R$ {calcValorAReceber(selectedVenda).toFixed(2).replace('.', ',')}</p>
                    {selectedVenda.status_pagamento === 'pago_parcial' && selectedVenda.valor_parcial_pago > 0 && (
                      <p><strong>Saldo restante:</strong> R$ {(calcValorAReceber(selectedVenda) - parseFloat(selectedVenda.valor_parcial_pago || 0)).toFixed(2).replace('.', ',')}</p>
                    )}
                    {selectedVenda.imagem_contato_duplo && (
                      <div style={{ marginTop: '1rem' }}>
                        <p><strong>Print Contato Duplo:</strong></p>
                        <a
                          href={getImagemContatoDuploUrl(selectedVenda.imagem_contato_duplo)}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-block',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            border: '1px solid var(--border-color, #e5e7eb)',
                            marginTop: '0.5rem'
                          }}
                        >
                          <img
                            src={getImagemContatoDuploUrl(selectedVenda.imagem_contato_duplo)}
                            alt="Print Contato Duplo"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '300px',
                              display: 'block',
                              objectFit: 'contain'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentNode.innerHTML = '<div style="padding: 1rem; background: var(--bg-secondary, #f3f4f6); border-radius: 6px; color: var(--texto-secundario, #6b7280);">Imagem não disponível</div>';
                            }}
                          />
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                <form onSubmit={handleUpdatePagamento}>
                  <div className="form-group">
                    <label htmlFor="valor_total">Valor Total da Venda (R$)</label>
                    <input
                      type="text"
                      id="valor_total"
                      name="valor_total"
                      value={pagamentoData.valor_total}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '');
                        setPagamentoData(prev => ({ ...prev, valor_total: v ? (parseFloat(v) / 100).toFixed(2).replace('.', ',') : '' }));
                      }}
                      placeholder="0,00"
                      className="form-control"
                    />
                    <small>Corrija o valor se foi informado errado na venda.</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="status_pagamento">Status de Pagamento *</label>
                    <select
                      id="status_pagamento"
                      name="status_pagamento"
                      value={pagamentoData.status_pagamento}
                      onChange={handlePagamentoChange}
                      required
                      className="form-control"
                    >
                      <option value="a_pagar">A Pagar</option>
                      <option value="pago_parcial">Pago Parcial</option>
                      <option value="pago">Pago</option>
                      <option value="estornado">Estornado</option>
                      <option value="contato_duplo">Contato Duplo</option>
                      <option value="ordem_duplicada">Ordem Duplicada</option>
                    </select>
                  </div>

                  {pagamentoData.status_pagamento === 'pago_parcial' && (
                    <>
                      <div className="form-group">
                        <label htmlFor="data_pagamento_parcial">Data do Pagamento Parcial *</label>
                        <input
                          type="date"
                          id="data_pagamento_parcial"
                          name="data_pagamento_parcial"
                          value={pagamentoData.data_pagamento_parcial || getTodayBrasilia()}
                          onChange={handlePagamentoChange}
                          required
                          className="form-control"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="valor_parcial_pago">Valor Parcial Pago (R$) *</label>
                        <input
                          type="text"
                          id="valor_parcial_pago"
                          name="valor_parcial_pago"
                          value={pagamentoData.valor_parcial_pago}
                          onChange={(e) => {
                            const v = e.target.value.replace(/\D/g, '');
                            setPagamentoData(prev => ({ ...prev, valor_parcial_pago: v ? (parseFloat(v) / 100).toFixed(2).replace('.', ',') : '' }));
                          }}
                          placeholder="0,00"
                          className="form-control"
                          required
                        />
                        <small>Valor já pago para abatimento. Saldo restante: R$ {(() => {
                          const vt = parseFloat(String(pagamentoData.valor_total || selectedVenda.valor_total).replace(',', '.')) || 0;
                          const perc = getPercentualVendedor(selectedVenda.vendedor, selectedVenda.tipo_cliente);
                          const vp = parseFloat(String(pagamentoData.valor_parcial_pago).replace(',', '.')) || 0;
                          const receber = vt * (perc / 100);
                          return Math.max(0, receber - vp).toFixed(2).replace('.', ',');
                        })()}</small>
                      </div>
                    </>
                  )}

                  {pagamentoData.status_pagamento === 'pago' && (
                    <div className="form-group">
                      <label htmlFor="data_pagamento">Data do Pagamento *</label>
                      <input
                        type="date"
                        id="data_pagamento"
                        name="data_pagamento"
                        value={pagamentoData.data_pagamento || getTodayBrasilia()}
                        onChange={handlePagamentoChange}
                        required
                        className="form-control"
                      />
                    </div>
                  )}

                  <div className="modal-actions">
                    <button type="button" onClick={closePagamento} className="btn-secondary">
                      Cancelar
                    </button>
                    <button type="submit" className="btn-primary" disabled={updating}>
                      {updating ? 'Salvando...' : 'Salvar'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modal: visualizar print Contato Duplo (vendedor/supervisor/admin) */}
        {vendaPrintContatoDuplo && (() => {
          const printUrl = getImagemContatoDuploUrl(vendaPrintContatoDuplo.imagem_contato_duplo);
          return (
            <div className="modal-overlay" onClick={() => setVendaPrintContatoDuplo(null)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '720px' }}>
                <div className="modal-header">
                  <h2>Print Contato Duplo — Venda #{vendaPrintContatoDuplo.id}</h2>
                  <button type="button" className="modal-close" onClick={() => setVendaPrintContatoDuplo(null)}>✕</button>
                </div>
                <div className="modal-body">
                  <div className="venda-info" style={{ marginBottom: '1rem' }}>
                    <p><strong>Cliente:</strong> {vendaPrintContatoDuplo.nome_cliente || vendaPrintContatoDuplo.razao_social}</p>
                    <p><strong>Documento:</strong> {formatDocumento(vendaPrintContatoDuplo.documento)}</p>
                    <p><strong>Status:</strong> {getStatusPagamentoText(vendaPrintContatoDuplo.status_pagamento)}</p>
                  </div>
                  {printUrl ? (
                    <a
                      href={printUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'block',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: '1px solid var(--border-color, #e5e7eb)',
                        textAlign: 'center'
                      }}
                    >
                      <img
                        src={printUrl}
                        alt="Print Contato Duplo"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '70vh',
                          display: 'block',
                          margin: '0 auto',
                          objectFit: 'contain'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentNode.innerHTML = '<div style="padding: 1.5rem; background: var(--bg-secondary, #f3f4f6); border-radius: 6px; color: var(--texto-secundario, #6b7280);">Imagem não disponível</div>';
                        }}
                      />
                    </a>
                  ) : (
                    <p style={{ color: 'var(--texto-secundario)' }}>Nenhum print disponível para esta venda.</p>
                  )}
                  <div className="modal-actions" style={{ marginTop: '1.25rem' }}>
                    {printUrl && (
                      <a
                        href={printUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary"
                        style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                      >
                        Abrir em nova aba
                      </a>
                    )}
                    <button type="button" className="btn-secondary" onClick={() => setVendaPrintContatoDuplo(null)}>
                      Fechar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Modal: Editar Pagamento do Supervisor */}
        {selectedVendaSupervisor && (() => {
          const supervisorId = selectedVendaSupervisor.vendedor?.supervisor_id;
          const supervisorInfo = supervisorId ? supervisoresMap[supervisorId] : null;
          const percentualSupervisor = supervisorInfo ? supervisorInfo.percentual_equipe : 0;
          const valorAReceberVendedor = calcValorAReceber(selectedVendaSupervisor);
          const valorMaxSupervisor = calcComissaoSupervisorEquipe(selectedVendaSupervisor, percentualSupervisor);
          const valorPagoSupervisor = parseFloat(selectedVendaSupervisor.valor_pago_supervisor || 0);
          const saldoSupervisor = Math.max(0, valorMaxSupervisor - valorPagoSupervisor);
          
          return (
          <div className="modal-overlay" onClick={() => setSelectedVendaSupervisor(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Pagamento do Supervisor</h2>
                <button className="modal-close" onClick={() => setSelectedVendaSupervisor(null)}>✕</button>
              </div>
              
              <div className="modal-body">
                <div className="form-group">
                  <label>Venda #{selectedVendaSupervisor.id}</label>
                  <div className="venda-info">
                    <p><strong>Cliente:</strong> {selectedVendaSupervisor.nome_cliente || selectedVendaSupervisor.razao_social}</p>
                    <p><strong>Vendedor:</strong> {selectedVendaSupervisor.vendedor?.nome || 'N/A'}</p>
                    <p><strong>Supervisor:</strong> {supervisorInfo?.nome || 'N/A'}</p>
                    <p><strong>PIX (supervisor):</strong> {supervisorInfo?.pix ? (
                      <span className="pix-value">{supervisorInfo.pix}</span>
                    ) : (
                      <span className="pix-empty">Não cadastrado</span>
                    )}</p>
                    <p><strong>Valor Total da Venda:</strong> R$ {parseFloat(selectedVendaSupervisor.valor_total || 0).toFixed(2).replace('.', ',')}</p>
                    <p><strong>Valor a receber vendedor ({getPercentualVendedor(selectedVendaSupervisor.vendedor, selectedVendaSupervisor.tipo_cliente)}%):</strong> R$ {valorAReceberVendedor.toFixed(2).replace('.', ',')}</p>
                    <p style={{ color: 'var(--accent-copper)' }}><strong>Comissão supervisor ({percentualSupervisor}% sobre o valor da venda):</strong> R$ {valorMaxSupervisor.toFixed(2).replace('.', ',')}</p>
                    <p style={{ color: 'var(--success)' }}><strong>Saldo a receber supervisor:</strong> R$ {saldoSupervisor.toFixed(2).replace('.', ',')}</p>
                    {selectedVendaSupervisor.status_pagamento_supervisor === 'pago_parcial' && valorPagoSupervisor > 0 && (
                      <p><strong>Já pago ao supervisor:</strong> R$ {valorPagoSupervisor.toFixed(2).replace('.', ',')}</p>
                    )}
                  </div>
                </div>

                <form onSubmit={handleUpdatePagamentoSupervisor}>
                  <div className="form-group">
                    <label htmlFor="status_pagamento_supervisor">Status de Pagamento (Supervisor) *</label>
                    <select
                      id="status_pagamento_supervisor"
                      name="status_pagamento_supervisor"
                      value={pagamentoSupervisorData.status_pagamento_supervisor}
                      onChange={(e) => setPagamentoSupervisorData(prev => ({ ...prev, status_pagamento_supervisor: e.target.value }))}
                      required
                      className="form-control"
                    >
                      <option value="a_pagar">A Pagar</option>
                      <option value="pago_parcial">Pago Parcial</option>
                      <option value="pago">Pago</option>
                      <option value="estornado">Estornado</option>
                      <option value="contato_duplo">Contato Duplo</option>
                      <option value="ordem_duplicada">Ordem Duplicada</option>
                    </select>
                  </div>

                  {pagamentoSupervisorData.status_pagamento_supervisor === 'pago_parcial' && (
                    <>
                      <div className="form-group">
                        <label htmlFor="data_pagamento_supervisor">Data do Pagamento Parcial *</label>
                        <input
                          type="date"
                          id="data_pagamento_supervisor"
                          name="data_pagamento_supervisor"
                          value={pagamentoSupervisorData.data_pagamento_supervisor || getTodayBrasilia()}
                          onChange={(e) => setPagamentoSupervisorData(prev => ({ ...prev, data_pagamento_supervisor: e.target.value }))}
                          required
                          className="form-control"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="valor_pago_supervisor">Valor Pago ao Supervisor (R$) *</label>
                        <input
                          type="text"
                          id="valor_pago_supervisor"
                          name="valor_pago_supervisor"
                          value={pagamentoSupervisorData.valor_pago_supervisor}
                          onChange={(e) => {
                            const v = e.target.value.replace(/\D/g, '');
                            setPagamentoSupervisorData(prev => ({ ...prev, valor_pago_supervisor: v ? (parseFloat(v) / 100).toFixed(2).replace('.', ',') : '' }));
                          }}
                          placeholder="0,00"
                          className="form-control"
                          required
                        />
                        <small>Saldo restante: R$ {saldoSupervisor.toFixed(2).replace('.', ',')}</small>
                      </div>
                    </>
                  )}

                  {pagamentoSupervisorData.status_pagamento_supervisor === 'pago' && (
                    <div className="form-group">
                      <label htmlFor="data_pagamento_supervisor_total">Data do Pagamento *</label>
                      <input
                        type="date"
                        id="data_pagamento_supervisor_total"
                        name="data_pagamento_supervisor_total"
                        value={pagamentoSupervisorData.data_pagamento_supervisor || getTodayBrasilia()}
                        onChange={(e) => setPagamentoSupervisorData(prev => ({ ...prev, data_pagamento_supervisor: e.target.value }))}
                        required
                        className="form-control"
                      />
                    </div>
                  )}

                  <div className="modal-actions">
                    <button type="button" onClick={() => setSelectedVendaSupervisor(null)} className="btn-secondary">
                      Cancelar
                    </button>
                    <button type="submit" className="btn-primary" disabled={updating}>
                      {updating ? 'Salvando...' : 'Salvar'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          );
        })()}

        {/* Modal: Gerar Comprovante */}
        {showComprovante && (() => {
          const vendasPagas = (vendasComprovante.length ? vendasComprovante : vendas).filter(v => {
            // Filtrar apenas pagas ou pagas parcial
            if (v.status_pagamento !== 'pago' && v.status_pagamento !== 'pago_parcial') return false;
            // Filtrar por mês/ano baseado na data de pagamento correta
            // pago_parcial → data_pagamento_parcial | pago → data_pagamento
            const dataPagamento = v.status_pagamento === 'pago_parcial' ? v.data_pagamento_parcial : v.data_pagamento;
            if (!dataPagamento) return false;
            return dataIsoEstaNoPeriodo(dataPagamento, comprovanteMes, comprovanteAno);
          });
          const vendasFiltradas = comprovanteModo === 'geral'
            ? vendasPagas
            : vendasPagas.filter(v => Number(v.vendedor_id ?? v.vendedor?.id) === Number(comprovanteModo));
          const vendedorSelecionado = comprovanteModo !== 'geral'
            ? (vendedores.find(v => Number(v.id) === Number(comprovanteModo))
              || (Number(comprovanteModo) === Number(user?.id) ? { id: user.id, nome: user.nome } : null))
            : null;
          const { inicio: periodoCompInicio, fim: periodoCompFim } = getPeriodoComercialExtk(comprovanteMes, comprovanteAno);
          let totalFiltrado = 0;
          vendasFiltradas.forEach(v => {
            const vid = v.vendedor_id ?? v.vendedor?.id;
            const isSub = user?.role === 'supervisor' && Number(vid) !== Number(user?.id);
            const perc = parseFloat(user?.percentual_equipe) || 0;
            if (v.status_pagamento === 'pago') totalFiltrado += getValorAReceberExibido(v, user);
            else totalFiltrado += isSub ? getValorSupervisorPagoParcialProporcional(v, perc) : parseFloat(v.valor_parcial_pago || 0);
          });
          const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' };
          const thStyle = { padding: '8px', textAlign: 'left', border: '1px solid var(--primary-dark)' };
          const tdStyle = { padding: '8px', border: '1px solid #e5e7eb', color: '#1e293b' };
          const showColunaVendedor = comprovanteModo === 'geral' && !isVendedorOnly;
          return (
          <div className="modal-overlay" onClick={() => setShowComprovante(false)}>
            <div className="modal-content modal-comprovante" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
              <div className="modal-header">
                <h2>🧾 Comprovante de Pagamentos</h2>
                <button type="button" className="modal-close" onClick={() => setShowComprovante(false)}>✕</button>
              </div>
              <div className="modal-body">
                {loadingComprovante && (
                  <p style={{ marginBottom: '1rem', color: 'var(--texto-secundario)' }}>Carregando vendas do período...</p>
                )}
                <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  {!isVendedorOnly && (
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600, fontSize: '0.9rem' }}>Tipo de comprovante:</label>
                      <select
                        value={comprovanteModo}
                        onChange={(e) => setComprovanteModo(e.target.value)}
                        className="filter-select"
                        style={{ minWidth: '200px' }}
                      >
                        <option value="geral">{isSupervisor ? '📋 Geral (equipe)' : '📋 Geral (todos)'}</option>
                        {vendedores.map(vend => (
                          <option key={vend.id} value={String(vend.id)}>👤 {vend.nome}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600, fontSize: '0.9rem' }}>Mês:</label>
                    <select
                      value={comprovanteMes}
                      onChange={(e) => setComprovanteMes(Number(e.target.value))}
                      className="filter-select"
                      style={{ minWidth: '140px' }}
                    >
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                        <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('pt-BR', { month: 'long' })}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600, fontSize: '0.9rem' }}>Ano:</label>
                    <select
                      value={comprovanteAno}
                      onChange={(e) => setComprovanteAno(Number(e.target.value))}
                      className="filter-select"
                      style={{ minWidth: '90px' }}
                    >
                      {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <p style={{ marginBottom: '1rem', fontSize: '0.85rem', color: 'var(--texto-secundario)' }}>
                  {isVendedorOnly
                    ? 'Selecione o período. Use o botão para imprimir ou salvar PDF.'
                    : 'Filtre por vendedor e período. Use o botão para imprimir ou salvar PDF.'}
                </p>
                <div id="comprovante-print-area" ref={comprovanteRef} className="comprovante-print-area" style={{
                  background: '#fff', color: '#333', padding: '2rem', fontFamily: 'Arial, sans-serif',
                  border: '1px solid #ddd', borderRadius: '8px'
                }}>
                  <div style={{ textAlign: 'center', marginBottom: '1.5rem', borderBottom: '2px solid var(--primary)', paddingBottom: '1rem' }}>
                    <img src={apiConfigService.getLogoUrl()} alt="Logo" style={{ maxHeight: '60px', marginBottom: '0.5rem' }} decoding="async" onError={(e) => { e.target.style.display = 'none'; }} />
                    <h2 style={{ margin: 0, color: 'var(--primary-dark)' }}>Comprovante de Pagamentos</h2>
                    <p style={{ margin: '0.5rem 0 0', fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' }}>
                      Período comercial: {formatDateForDisplay(periodoCompInicio)} a {formatDateForDisplay(periodoCompFim)} ({new Date(2000, comprovanteMes - 1, 1).toLocaleString('pt-BR', { month: 'long' })} / {comprovanteAno})
                    </p>
                    {vendedorSelecionado && (
                      <p style={{ margin: '0.25rem 0 0', fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>
                        Vendedor: {vendedorSelecionado.nome}
                      </p>
                    )}
                    <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem', color: '#334155' }}>Emitido em: {new Date().toLocaleString('pt-BR')}</p>
                  </div>
                  <table style={tableStyle}>
                    <thead>
                      <tr style={{ background: 'var(--primary)', color: '#fff' }}>
                        <th style={{ ...thStyle }}>ID</th>
                        <th style={{ ...thStyle }}>Cliente</th>
                        {showColunaVendedor && <th style={{ ...thStyle }}>Vendedor</th>}
                        <th style={{ ...thStyle, textAlign: 'right' }}>Valor a Receber</th>
                        <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Valor Pago</th>
                        <th style={{ ...thStyle, textAlign: 'center' }}>Data Pag. Parcial</th>
                        <th style={{ ...thStyle, textAlign: 'center' }}>Data Pag. Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendasFiltradas.map(v => {
                        const vid = v.vendedor_id ?? v.vendedor?.id;
                        const isSub = user?.role === 'supervisor' && Number(vid) !== Number(user?.id);
                        const perc = parseFloat(user?.percentual_equipe) || 0;
                        const valorParcialExibido = isSub ? getValorSupervisorPagoParcialProporcional(v, perc) : parseFloat(v.valor_parcial_pago || 0);
                        return (
                          <tr key={v.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={tdStyle}>#{v.id}</td>
                            <td style={tdStyle}>{v.nome_cliente || v.razao_social}</td>
                            {showColunaVendedor && <td style={tdStyle}>{v.vendedor?.nome || 'N/A'}</td>}
                            <td style={{ ...tdStyle, textAlign: 'right' }}>R$ {getValorAReceberExibido(v, user).toFixed(2).replace('.', ',')}</td>
                            <td style={{ ...tdStyle, textAlign: 'center' }}>{v.status_pagamento === 'pago' ? 'Pago' : 'Pago Parcial'}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                              {v.status_pagamento === 'pago'
                                ? ('R$ ' + getValorAReceberExibido(v, user).toFixed(2).replace('.', ','))
                                : ('R$ ' + valorParcialExibido.toFixed(2).replace('.', ','))}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'center' }}>{v.data_pagamento_parcial ? formatDateForDisplay(v.data_pagamento_parcial) : '-'}</td>
                            <td style={{ ...tdStyle, textAlign: 'center' }}>{v.data_pagamento ? formatDateForDisplay(v.data_pagamento) : '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {vendasFiltradas.length === 0 && (
                    <p className="comprovante-empty" style={{ padding: '2rem', textAlign: 'center', color: '#475569' }}>
                      {comprovanteModo === 'geral' ? 'Nenhum pagamento ou pagamento parcial no filtro atual.' : 'Nenhum pagamento ou pagamento parcial para este vendedor.'}
                    </p>
                  )}
                  {vendasFiltradas.length > 0 && (
                    <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '2px solid var(--primary)', fontSize: '1rem', fontWeight: 'bold', color: '#1e293b' }}>
                      Total: R$ {totalFiltrado.toFixed(2).replace('.', ',')}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                  <button type="button" className="btn-primary" onClick={imprimirComprovante}>
                    🖨️ Imprimir / Salvar PDF
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => setShowComprovante(false)}>Fechar</button>
                </div>
              </div>
            </div>
          </div>
          );
        })()}

        {/* Modal: Quanto o vendedor tem a receber */}
        {(operadoraResumo || loadingResumo) && (
          <div className="modal-overlay" onClick={loadingResumo ? undefined : closeResumoModal}>
            <div className="modal-content modal-resumo" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Valor a receber por vendedor</h2>
                <button type="button" className="modal-close" onClick={closeResumoModal} disabled={loadingResumo}>✕</button>
              </div>
              <div className="modal-body">
                {loadingResumo ? (
                  <div className="loading">Carregando resumo...</div>
                ) : operadoraResumo ? (
                  <>
                    <p className="resumo-operadora-nome"><strong>Operadora:</strong> {operadoraResumo.operadora_nome}</p>
                    <div className="resumo-periodo-filters">
                      <label>Mês:</label>
                      <select
                        value={resumoMes}
                        onChange={(e) => applyResumoPeriodo(Number(e.target.value), resumoAno)}
                        className="form-control resumo-select"
                      >
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                          <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('pt-BR', { month: 'long' })}</option>
                        ))}
                      </select>
                      <label>Ano:</label>
                      <select
                        value={resumoAno}
                        onChange={(e) => applyResumoPeriodo(resumoMes, Number(e.target.value))}
                        className="form-control resumo-select"
                      >
                        {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                    <div className="resumo-modo-toggle" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ marginRight: '0.25rem', color: '#4b5563', fontSize: '0.9rem' }}>Exibir:</span>
                      <button
                        type="button"
                        className={resumoModo === 'a_receber' ? 'btn-primary' : 'btn-secondary'}
                        onClick={() => switchResumoModo('a_receber')}
                        disabled={loadingResumo}
                      >
                        A receber
                      </button>
                      <button
                        type="button"
                        className={resumoModo === 'pagos' ? 'btn-primary' : 'btn-secondary'}
                        onClick={() => switchResumoModo('pagos')}
                        disabled={loadingResumo}
                      >
                        Pagos
                      </button>
                    </div>
                    <h3 className="resumo-subtitle">Valor mensal</h3>
                    {operadoraResumo.resumo && operadoraResumo.resumo.length > 0 ? (
                      <>
                        <h4 className="resumo-section-title">Vendedores</h4>
                        <div className="resumo-table-container">
                          <div className="resumo-table-wrapper">
                            <table className="resumo-table">
                              <thead>
                                <tr>
                                  <th>Vendedor</th>
                                  <th>PIX (pagamento)</th>
                                  <th>Valor da Venda</th>
                                  <th>Valor Comissão</th>
                                  <th>{resumoLabelColSaldo}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {operadoraResumo.resumo.map((r) => (
                                  <tr key={r.vendedor_id}>
                                    <td>{r.vendedor_nome}</td>
                                    <td className="pix-cell">{r.vendedor_pix || '-'}</td>
                                    <td>R$ {Number(r.total_valor_venda || 0).toFixed(2).replace('.', ',')}</td>
                                    <td>R$ {Number(r.total_valor_comissao || 0).toFixed(2).replace('.', ',')}</td>
                                    <td>R$ {Number(r.total_a_receber || 0).toFixed(2).replace('.', ',')}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr>
                                  <td><strong>Total Vendedores</strong></td>
                                  <td></td>
                                  <td><strong>R$ {Number(operadoraResumo.total_geral_venda || 0).toFixed(2).replace('.', ',')}</strong></td>
                                  <td><strong>R$ {Number(operadoraResumo.total_geral_comissao || 0).toFixed(2).replace('.', ',')}</strong></td>
                                  <td><strong>R$ {Number(operadoraResumo.total_geral_a_receber || 0).toFixed(2).replace('.', ',')}</strong></td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                        {!isVendedorOnly && operadoraResumo.supervisores && operadoraResumo.supervisores.length > 0 && (
                          <>
                            <h4 className="resumo-section-title" style={{ marginTop: '1.5rem' }}>Supervisores</h4>
                            <div className="resumo-table-container">
                              <div className="resumo-table-wrapper">
                                <table className="resumo-table">
                                  <thead>
                                    <tr>
                                      <th>Supervisor</th>
                                      <th>PIX</th>
                                      <th>% Equipe</th>
                                      <th>Vendas Próprias</th>
                                      <th>{resumoLabelProprias}</th>
                                      <th>Vendas Equipe</th>
                                      <th>Comissão Equipe</th>
                                      <th>{resumoLabelEquipe}</th>
                                      <th><strong>{resumoLabelTotal}</strong></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {operadoraResumo.supervisores.map((s) => {
                                      const totalAReceber = (s.total_a_receber_proprias || 0) + (s.total_a_receber_supervisor || 0);
                                      return (
                                      <tr key={s.supervisor_id}>
                                        <td>{s.supervisor_nome}</td>
                                        <td className="pix-cell">{s.supervisor_pix || '-'}</td>
                                        <td>{Number(s.percentual_equipe || 0)}%</td>
                                        <td>{s.vendedor_id ? `R$ ${Number(s.total_vendas_proprias || 0).toFixed(2).replace('.', ',')}` : '-'}</td>
                                        <td>{s.vendedor_id ? `R$ ${Number(s.total_a_receber_proprias || 0).toFixed(2).replace('.', ',')}` : '-'}</td>
                                        <td>R$ {Number(s.total_valor_venda_equipe || 0).toFixed(2).replace('.', ',')}</td>
                                        <td>R$ {Number(s.total_comissao_supervisor || 0).toFixed(2).replace('.', ',')}</td>
                                        <td>R$ {Number(s.total_a_receber_supervisor || 0).toFixed(2).replace('.', ',')}</td>
                                        <td><strong>R$ {Number(totalAReceber).toFixed(2).replace('.', ',')}</strong></td>
                                      </tr>
                                      );
                                    })}
                                  </tbody>
                                  <tfoot>
                                    <tr>
                                      <td><strong>Total</strong></td>
                                      <td></td>
                                      <td></td>
                                      <td></td>
                                      <td><strong>R$ {Number(operadoraResumo.supervisores.reduce((sum, s) => sum + (s.total_a_receber_proprias || 0), 0)).toFixed(2).replace('.', ',')}</strong></td>
                                      <td></td>
                                      <td><strong>R$ {Number(operadoraResumo.total_geral_comissao_supervisor || 0).toFixed(2).replace('.', ',')}</strong></td>
                                      <td><strong>R$ {Number(operadoraResumo.total_geral_a_receber_supervisor || 0).toFixed(2).replace('.', ',')}</strong></td>
                                      <td><strong>R$ {Number(operadoraResumo.supervisores.reduce((sum, s) => sum + (s.total_a_receber_proprias || 0) + (s.total_a_receber_supervisor || 0), 0)).toFixed(2).replace('.', ',')}</strong></td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <p className="resumo-empty">
                        {resumoModo === 'pagos'
                          ? 'Nenhum pagamento concluído neste período para esta operadora.'
                          : 'Nenhuma venda a pagar para esta operadora no mês.'}
                      </p>
                    )}
                    {operadoraResumo.por_semana && operadoraResumo.por_semana.length > 0 && (
                      <>
                        <h3 className="resumo-subtitle">Por semana</h3>
                        {operadoraResumo.por_semana.map((s) => (
                          <div key={s.semana} className="resumo-semana-block">
                            <h4 className="resumo-semana-label">
                              {s.label} — Vendas: R$ {Number(s.total_valor_venda_semana || 0).toFixed(2).replace('.', ',')} | Comissão: R$ {Number(s.total_valor_comissao_semana || 0).toFixed(2).replace('.', ',')} | {resumoLabelSemanaSaldo}: R$ {Number(s.total_a_receber_semana || 0).toFixed(2).replace('.', ',')}
                              {!isVendedorOnly && s.supervisores && s.supervisores.length > 0 && (
                                <> | Supervisores: R$ {Number(s.total_comissao_supervisor_semana || 0).toFixed(2).replace('.', ',')}</>
                              )}
                            </h4>
                            {s.resumo && s.resumo.length > 0 ? (
                              <>
                                <div className="resumo-table-container">
                                  <div className="resumo-table-wrapper">
                                    <table className="resumo-table resumo-table-semana">
                                      <thead>
                                        <tr>
                                          <th>Vendedor</th>
                                          <th>PIX</th>
                                          <th>Valor da Venda</th>
                                          <th>Valor Comissão</th>
                                          <th>{resumoLabelColSaldo}</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {s.resumo.map((r) => (
                                          <tr key={r.vendedor_id}>
                                            <td>{r.vendedor_nome}</td>
                                            <td className="pix-cell">{r.vendedor_pix || '-'}</td>
                                            <td>R$ {Number(r.total_valor_venda || 0).toFixed(2).replace('.', ',')}</td>
                                            <td>R$ {Number(r.total_valor_comissao || 0).toFixed(2).replace('.', ',')}</td>
                                            <td>R$ {Number(r.total_a_receber || 0).toFixed(2).replace('.', ',')}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                                {!isVendedorOnly && s.supervisores && s.supervisores.length > 0 && (
                                  <div className="resumo-table-container" style={{ marginTop: '0.5rem' }}>
                                    <div className="resumo-table-wrapper">
                                      <table className="resumo-table resumo-table-semana">
                                        <thead>
                                          <tr>
                                            <th>Supervisor</th>
                                            <th>PIX</th>
                                            <th>% Equipe</th>
                                            <th>Vendas Próprias</th>
                                            <th>{resumoLabelProprias}</th>
                                            <th>Vendas Equipe</th>
                                            <th>Comissão Equipe</th>
                                            <th>{resumoLabelTotal}</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {s.supervisores.map((sup) => {
                                            const totalAReceber = (sup.total_a_receber_proprias || 0) + (sup.total_a_receber_supervisor || 0);
                                            return (
                                            <tr key={sup.supervisor_id}>
                                              <td>{sup.supervisor_nome}</td>
                                              <td className="pix-cell">{sup.supervisor_pix || '-'}</td>
                                              <td>{Number(sup.percentual_equipe || 0)}%</td>
                                              <td>{sup.vendedor_id ? `R$ ${Number(sup.total_vendas_proprias || 0).toFixed(2).replace('.', ',')}` : '-'}</td>
                                              <td>{sup.vendedor_id ? `R$ ${Number(sup.total_a_receber_proprias || 0).toFixed(2).replace('.', ',')}` : '-'}</td>
                                              <td>R$ {Number(sup.total_valor_venda_equipe || 0).toFixed(2).replace('.', ',')}</td>
                                              <td>R$ {Number(sup.total_comissao_supervisor || 0).toFixed(2).replace('.', ',')}</td>
                                              <td><strong>R$ {Number(totalAReceber).toFixed(2).replace('.', ',')}</strong></td>
                                            </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                              </>
                            ) : (
                              <p className="resumo-empty-inline">Nenhuma venda nesta semana.</p>
                            )}
                          </div>
                        ))}
                      </>
                    )}
                  </>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>

      {showExportCsvModal && (
        <div className="modal-overlay" onClick={() => setShowExportCsvModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h2>Exportar CSV</h2>
              <button type="button" className="modal-close" onClick={() => setShowExportCsvModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '1rem', color: 'var(--texto-secundario)', fontSize: '0.9rem' }}>
                Escolha o recorte da exportação. Os filtros de período, canal e status já aplicados serão mantidos.
              </p>
              <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 500 }}>
                  <input
                    type="radio"
                    name="exportCsvModo"
                    checked={exportCsvModo === 'equipe'}
                    onChange={() => setExportCsvModo('equipe')}
                  />
                  Toda a equipe (uma planilha)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 500 }}>
                  <input
                    type="radio"
                    name="exportCsvModo"
                    checked={exportCsvModo === 'meu'}
                    onChange={() => setExportCsvModo('meu')}
                  />
                  Somente o meu
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 500 }}>
                  <input
                    type="radio"
                    name="exportCsvModo"
                    checked={exportCsvModo === 'por_vendedor'}
                    onChange={() => setExportCsvModo('por_vendedor')}
                  />
                  Por vendedor (arquivos separados)
                </label>
              </div>
              {exportCsvModo === 'por_vendedor' && (
                <div style={{ marginTop: '1rem', padding: '0.75rem', border: '1px solid var(--border-color)', borderRadius: '8px', maxHeight: '240px', overflowY: 'auto' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <strong style={{ fontSize: '0.9rem' }}>Vendedores</strong>
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <button
                        type="button"
                        style={{ fontSize: '0.8rem', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', color: 'var(--texto-secundario)', padding: 0 }}
                        onClick={() => setExportCsvVendedoresIds(vendedores.map((v) => Number(v.id)))}
                      >
                        Todos
                      </button>
                      <button
                        type="button"
                        style={{ fontSize: '0.8rem', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', color: 'var(--texto-secundario)', padding: 0 }}
                        onClick={() => setExportCsvVendedoresIds([])}
                      >
                        Limpar
                      </button>
                    </div>
                  </div>
                  {vendedores.map((vend) => {
                    const id = Number(vend.id);
                    const checked = exportCsvVendedoresIds.includes(id);
                    return (
                      <label
                        key={vend.id}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', padding: '0.25rem 0', cursor: 'pointer', fontSize: '0.9rem' }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            setExportCsvVendedoresIds((prev) =>
                              e.target.checked ? [...prev, id] : prev.filter((x) => x !== id)
                            );
                          }}
                        />
                        {vend.nome}{Number(vend.id) === Number(user?.id) ? ' (eu)' : ''}
                      </label>
                    );
                  })}
                </div>
              )}
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginTop: '1rem',
                  cursor: 'pointer',
                  fontWeight: 500,
                  fontSize: '0.9rem'
                }}
              >
                <input
                  type="checkbox"
                  checked={exportCsvIncluirAReceber}
                  onChange={(e) => setExportCsvIncluirAReceber(e.target.checked)}
                />
                Incluir coluna Valor a Receber
              </label>
              <div className="modal-actions" style={{ marginTop: '1.25rem', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button type="button" className="btn-secondary" onClick={() => setShowExportCsvModal(false)}>
                  Cancelar
                </button>
                <button type="button" className="btn-primary" onClick={confirmarExportCsvSupervisor}>
                  Baixar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showImportCnpjModal && importPreview?.conflitos?.length > 0 && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && resetImportFlow()}>
          <div className="modal-content import-xlsx-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Várias vendas para o mesmo CPF/CNPJ</h2>
              <button type="button" className="modal-close" onClick={resetImportFlow}>✕</button>
            </div>
            <div className="modal-body">
              <p>A coluna A da planilha só agrupa valores para somar; a venda é localizada pelo CPF/CNPJ. Escolha qual venda do CRM corresponde a cada linha (chave col. A).</p>
              {importPreview.conflitos.map((c) => (
                <div key={c.chave_grupo} className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>
                    Chave col. A: <strong>{c.chave_grupo}</strong> — {formatDocumento(c.documento_norm)}
                  </label>
                  <select
                    value={importResolucoes[c.chave_grupo] ?? String(c.candidatos[0]?.id ?? '')}
                    onChange={(e) => setImportResolucoes((prev) => ({ ...prev, [c.chave_grupo]: e.target.value }))}
                    className="form-control"
                  >
                    {c.candidatos.map((x) => (
                      <option key={x.id} value={x.id}>
                        #{x.id} — {x.nome_cliente || '—'} — R$ {Number(x.valor_total || 0).toFixed(2).replace('.', ',')}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <div className="modal-footer" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', padding: '1rem', borderTop: '1px solid #e5e7eb' }}>
              <button type="button" className="btn-secondary" onClick={resetImportFlow}>Cancelar</button>
              <button type="button" className="btn-primary" onClick={fecharModalCnpjEconfirmar}>Continuar</button>
            </div>
          </div>
        </div>
      )}

      {showImportConfirmModal && importPreview && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && resetImportFlow()}>
          <div className="modal-content import-xlsx-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px' }}>
            <div className="modal-header">
              <h2>Confirmar importação</h2>
              <button type="button" className="modal-close" onClick={resetImportFlow}>✕</button>
            </div>
            <div className="modal-body">
              <p><strong>Modo:</strong> {importModo === 'parcial' ? 'Pagamento parcial' : importModo === 'estorno' ? 'Baixa estorno' : importModo === 'extrato' ? 'Atualizar extrato' : 'Marcar como pago'}</p>
              <p><strong>Data de referência:</strong> {importPreview.data_ref_sugerida}</p>
              {importPreview.erros?.length > 0 && (
                <div className="import-xlsx-erros" style={{ background: '#fef2f2', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem' }}>
                  <strong>Erros (corrija a planilha antes de aplicar):</strong>
                  <ul style={{ margin: '0.5rem 0 0 1rem' }}>
                    {importPreview.erros.map((err, i) => (
                      <li key={i}>
                        {err.chave_grupo != null ? `Chave A ${err.chave_grupo}: ` : ''}
                        {err.sale_id != null ? `Venda #${err.sale_id}: ` : ''}
                        {err.mensagem}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div style={{ overflowX: 'auto' }}>
                <table className="vendas-pagamento-table" style={{ fontSize: '0.85rem', minWidth: '100%' }}>
                  <thead>
                    <tr>
                      <th>Chave col. A (soma)</th>
                      <th>Venda CRM</th>
                      <th>CPF/CNPJ</th>
                      <th>Soma col. C</th>
                      <th>Escolher venda</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(importPreview.grupos || []).map((g) => (
                      <tr key={g.chave_grupo}>
                        <td>{g.chave_grupo}</td>
                        <td>{g.sale_id != null ? `#${g.sale_id}` : '—'}</td>
                        <td>{formatDocumento(g.documento_norm)}</td>
                        <td>R$ {Number(g.somaC).toFixed(2).replace('.', ',')}</td>
                        <td>{g.requer_escolha_venda ? 'Sim' : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', padding: '1rem', borderTop: '1px solid #e5e7eb' }}>
              <button type="button" className="btn-secondary" onClick={resetImportFlow}>Cancelar</button>
              <button
                type="button"
                className="btn-primary"
                disabled={importLoading || (importPreview.erros?.length > 0) || !(importPreview.grupos?.length > 0)}
                onClick={executarImportApply}
              >
                {importLoading ? 'Aplicando…' : 'Aplicar importação'}
              </button>
            </div>
          </div>
        </div>
      )}

      {importResultado && (
        <div className="modal-overlay" onClick={() => setImportResultado(null)}>
          <div className="modal-content import-xlsx-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px' }}>
            <div className="modal-header">
              <h2>Importação concluída</h2>
              <button type="button" className="modal-close" onClick={() => setImportResultado(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p><strong>Data:</strong> {importResultado.data_ref}</p>
              <p><strong>Modo:</strong> {importResultado.modo === 'parcial' ? 'Pagamento parcial' : importResultado.modo === 'estorno' ? 'Baixa estorno' : importResultado.modo === 'extrato' ? 'Atualizar extrato' : 'Pago'}</p>
              <ul style={{ margin: '0.75rem 0 0 1rem', lineHeight: 1.5 }}>
                {(importResultado.aplicados || []).map((a) => (
                  <li key={`${a.chave_grupo}-${a.sale_id}`}>
                    Col. A <strong>{a.chave_grupo}</strong> → venda CRM <strong>#{a.sale_id}</strong> — {a.detalhe}
                  </li>
                ))}
              </ul>
            </div>
            <div className="modal-footer" style={{ padding: '1rem', borderTop: '1px solid #e5e7eb' }}>
              <button type="button" className="btn-primary" onClick={() => setImportResultado(null)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Valor a Receber por marca (ONDA / VISION) */}
      {(showResumoMarca || loadingResumoMarca) && (
        <div className="modal-overlay" onClick={closeResumoMarcaModal}>
          <div className="modal-content modal-resumo" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Valor a receber — {resumoMarca?.marca_nome || resumoMarcaLabel}</h2>
              <button type="button" className="modal-close" onClick={closeResumoMarcaModal}>✕</button>
            </div>
            <div className="modal-body">
              {loadingResumoMarca ? (
                <div className="loading">Carregando resumo {resumoMarcaLabel}...</div>
              ) : resumoMarca ? (
                <>
                  <p className="resumo-operadora-nome">
                    <strong>Marca:</strong> {resumoMarca.marca_nome || resumoMarcaLabel}
                  </p>
                  <p style={{ marginBottom: '0.75rem', color: 'var(--texto-secundario)', fontSize: '0.9rem' }}>
                    Período: {resumoMarca.periodo?.inicio || ''} a {resumoMarca.periodo?.fim || ''} (mês de conclusão).
                    Pagamento previsto em {String(resumoMarca.pagamento_previsto?.mes || '').padStart(2, '0')}/{resumoMarca.pagamento_previsto?.ano}.
                  </p>
                  <div className="resumo-periodo-filters">
                    <select
                      value={resumoMarcaMes}
                      onChange={(e) => applyResumoMarcaPeriodo(Number(e.target.value), resumoMarcaAno)}
                      className="form-control resumo-select"
                    >
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                        <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                      ))}
                    </select>
                    <select
                      value={resumoMarcaAno}
                      onChange={(e) => applyResumoMarcaPeriodo(resumoMarcaMes, Number(e.target.value))}
                      className="form-control resumo-select"
                    >
                      {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                  <div className="resumo-modo-toggle" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <button
                      type="button"
                      className={resumoMarcaModo === 'a_receber' ? 'btn-primary' : 'btn-secondary'}
                      onClick={() => applyResumoMarcaModo('a_receber')}
                    >
                      A receber
                    </button>
                    <button
                      type="button"
                      className={resumoMarcaModo === 'pagos' ? 'btn-primary' : 'btn-secondary'}
                      onClick={() => applyResumoMarcaModo('pagos')}
                    >
                      Já pagos
                    </button>
                  </div>
                  <h3 className="resumo-subtitle">Valor mensal</h3>
                  {resumoMarca.resumo && resumoMarca.resumo.length > 0 ? (
                    <>
                      <h4 className="resumo-section-title">Vendedores</h4>
                      <div className="resumo-table-container">
                        <div className="resumo-table-wrapper">
                          <table className="resumo-table">
                            <thead>
                              <tr>
                                <th>Vendedor</th>
                                <th>PIX</th>
                                <th>Valor da Venda</th>
                                <th>Valor Comissão</th>
                                <th>{resumoMarcaModo === 'pagos' ? 'Valor Pago' : 'Saldo a Receber'}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {resumoMarca.resumo.map((r) => (
                                <tr key={r.vendedor_id}>
                                  <td>{r.vendedor_nome}</td>
                                  <td className="pix-cell">{r.vendedor_pix || '-'}</td>
                                  <td>R$ {Number(r.total_valor_venda || 0).toFixed(2).replace('.', ',')}</td>
                                  <td>R$ {Number(r.total_valor_comissao || 0).toFixed(2).replace('.', ',')}</td>
                                  <td>R$ {Number(r.total_a_receber || 0).toFixed(2).replace('.', ',')}</td>
                                </tr>
                              ))}
                              <tr>
                                <td><strong>Total</strong></td>
                                <td />
                                <td><strong>R$ {Number(resumoMarca.total_geral_venda || 0).toFixed(2).replace('.', ',')}</strong></td>
                                <td><strong>R$ {Number(resumoMarca.total_geral_comissao || 0).toFixed(2).replace('.', ',')}</strong></td>
                                <td><strong>R$ {Number(resumoMarca.total_geral_a_receber || 0).toFixed(2).replace('.', ',')}</strong></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      {!isVendedorOnly && resumoMarca.supervisores && resumoMarca.supervisores.length > 0 && (
                        <>
                          <h4 className="resumo-section-title" style={{ marginTop: '1.5rem' }}>Supervisores</h4>
                          <div className="resumo-table-container">
                            <div className="resumo-table-wrapper">
                              <table className="resumo-table">
                                <thead>
                                  <tr>
                                    <th>Supervisor</th>
                                    <th>PIX</th>
                                    <th>% Equipe</th>
                                    <th>Vendas Próprias</th>
                                    <th>{resumoMarcaModo === 'pagos' ? 'Pago (Próprias)' : 'A Receber (Próprias)'}</th>
                                    <th>Vendas Equipe</th>
                                    <th>{resumoMarcaModo === 'pagos' ? 'Pago (Equipe)' : 'A Receber (Equipe)'}</th>
                                    <th><strong>{resumoMarcaModo === 'pagos' ? 'Total Pago' : 'Total a Receber'}</strong></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {resumoMarca.supervisores.map((s) => {
                                    const total = (s.total_a_receber_proprias || 0) + (s.total_a_receber_supervisor || 0);
                                    return (
                                      <tr key={s.supervisor_id}>
                                        <td>{s.supervisor_nome}</td>
                                        <td className="pix-cell">{s.supervisor_pix || '-'}</td>
                                        <td>{Number(s.percentual_equipe || 0)}%</td>
                                        <td>{s.vendedor_id ? `R$ ${Number(s.total_vendas_proprias || 0).toFixed(2).replace('.', ',')}` : '-'}</td>
                                        <td>{s.vendedor_id ? `R$ ${Number(s.total_a_receber_proprias || 0).toFixed(2).replace('.', ',')}` : '-'}</td>
                                        <td>R$ {Number(s.total_valor_venda_equipe || 0).toFixed(2).replace('.', ',')}</td>
                                        <td>R$ {Number(s.total_a_receber_supervisor || 0).toFixed(2).replace('.', ',')}</td>
                                        <td><strong>R$ {Number(total).toFixed(2).replace('.', ',')}</strong></td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <p className="resumo-empty">
                      {resumoMarcaModo === 'pagos'
                        ? `Nenhum pagamento concluído neste período para ${resumoMarca.marca_nome || resumoMarcaLabel}.`
                        : `Nenhuma venda ${resumoMarca.marca_nome || resumoMarcaLabel} a pagar neste mês de conclusão.`}
                    </p>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default VendasPagamento;
